/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useToast } from './components/ui/ToastContext';
import { Notebook, NoteCard, NOTE_COLORS, SortOption, CardType, DESK_THEMES } from './types';
import { NotebookCover, COVERS_PRESETS } from './components/notebook/NotebookCover';
import { NoteCardItem } from './components/all_notes/NoteCardItem';
import { NoteEditorInline } from './components/all_notes/NoteEditorInline';
import { RecycleBinModal } from './components/all_notes/RecycleBinModal';
import { CanvasPage } from './components/canvas/CanvasPage';
import { DesignStudioEditor } from './components/DesignStudioEditor';
import { StickyNotesDashboard } from './components/notebook/sticknotes/StickyNotesDashboard';
import LandingPage from './components/figjamlanding/LandingFile';
import { NoteboardWorkspace } from './components/noteboard/NoteboardWorkspace';
import { NotebookPageEditor } from './components/NotebookPageEditor';
import {
  BookOpen,
  FolderOpen,
  Heart,
  Tag,
  Clock,
  Search,
  Grid,
  List,
  Trash2,
  FileText,
  CheckSquare,
  Paintbrush,
  Volume2,
  Plus,
  ArrowLeft,
  Settings,
  Bell,
  Sun,
  Moon,
  ChevronRight,
  ChevronLeft,
  Columns,
  Compass,
  ShieldCheck,
  Edit3,
  X,
  Palette,
  Maximize,
  Minimize,
  Sparkles,
  Lock,
  Unlock,
  Share2,
  Info,
  MoreHorizontal,
  MoreVertical,
  Users,
  User,
  Camera,
  Video,
  Settings2,
  Paperclip,
  Check,
  ChevronDown
} from 'lucide-react';

const stripHtml = (html: string): string => {
  if (!html) return '';
  let text = html
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  text = text.replace(/<[^>]*>/g, ' ');
  text = text.replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ');
  return text.trim();
};

const LOCAL_STORAGE_NOTEBOOKS_KEY = 'notebook_clone_bookshelf';
const LOCAL_STORAGE_CARDS_KEY = 'notebook_clone_cards_catalog';
const LOCAL_STORAGE_DARK_MODE_KEY = 'notebook_clone_theme_dark';

// Pre-populated elements to give immediate premium feel
const DEFAULT_NOTEBOOKS: Notebook[] = [
  {
    id: 'book-1',
    title: 'My Notecards',
    coverStyle: { type: 'pattern', primaryColor: '#2c3e50', secondaryColor: '#34495e', patternName: 'grid' },
    createdAt: Date.now() - 5 * 24 * 60 * 60 * 1000,
  }
];

const DEFAULT_NOTE_CARDS: NoteCard[] = [
  {
    id: 'note-1',
    notebookId: 'book-1',
    type: 'text',
    title: 'Take notes, add files, create checklists, pen drawings, record audio, and do much more.',
    colorClass: NOTE_COLORS[0].class, // Yellow
    createdAt: Date.now() - 4 * 24 * 60 * 60 * 1000,
    updatedAt: Date.now(),
    isFavorite: false,
    isLocked: false,
    tags: [],
    textContent: 'Take notes, add files, create checklists, pen drawings, record audio, and do much more.',
  }
];

export default function App() {
  const { showToast, confirm } = useToast();
  // System State Management
  const [notebooks, setNotebooks] = useState<Notebook[]>([]);
  const [cards, setCards] = useState<NoteCard[]>([]);
  const [activeCardsList, setActiveCardsList] = useState<NoteCard[]>([]);

  useEffect(() => {
    setActiveCardsList(cards.filter((c) => !c.isDeleted));
  }, [cards]);
  const activeNotebooksList = notebooks.filter((b) => !b.isDeleted);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    const handleOpenNoteEvent = (e: Event) => {
      const customEvent = e as CustomEvent<{ id: string }>;
      // Load current cards array directly from cards state
      const targetCard = cards.find(c => c.id === customEvent.detail.id);
      if (targetCard) {
        setEditingCard(targetCard);
      }
    };
    window.addEventListener('open-note', handleOpenNoteEvent);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      window.removeEventListener('open-note', handleOpenNoteEvent);
    };
  }, [cards]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch((err) => {
          console.error(`Error attempting to exit fullscreen: ${err.message}`);
        });
      }
    }
  };

  // Active view constraints
  const [selectedNotebookId, setSelectedNotebookId] = useState<string | null>(null);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showSettingsSubMenu, setShowSettingsSubMenu] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);

  // Close settings dropdown on click outside
  useEffect(() => {
    if (!showSettingsDropdown) return;
    const closeDropdown = () => {
      setShowSettingsDropdown(false);
      setShowSettingsSubMenu(false);
    };
    window.addEventListener('click', closeDropdown);
    return () => window.removeEventListener('click', closeDropdown);
  }, [showSettingsDropdown]);
  // Default to pathname directly if valid, otherwise check hash fallback, otherwise landing
  const [activeTab, setActiveTab] = useState<'all_notes' | 'notebooks' | 'noteboards' | 'shared' | 'search' | 'reminders' | 'tags' | 'canvas' | 'sticky' | 'landing' | 'docs' | 'favorites'>(() => {
    let path = window.location.pathname.replace(/^\//, '');
    if (!path && window.location.hash) {
      path = window.location.hash.replace(/^#\/?/, '');
    }
    const validTabs = ['all_notes', 'notebooks', 'noteboards', 'shared', 'search', 'reminders', 'tags', 'canvas', 'sticky', 'landing', 'docs', 'favorites'];
    return (validTabs.includes(path) ? path : 'landing') as any;
  });

  // Keep browser history synced with active tab
  useEffect(() => {
    const currentPath = window.location.pathname.replace(/^\//, '');
    if (currentPath !== activeTab) {
      window.history.pushState(null, '', `/${activeTab}`);
    }
  }, [activeTab]);

  // Handle browser back/forward buttons and hash changes
  useEffect(() => {
    const handleNavigation = () => {
      let path = window.location.pathname.replace(/^\//, '');
      if (!path && window.location.hash) {
        path = window.location.hash.replace(/^#\/?/, '');
      }
      const validTabs = ['all_notes', 'notebooks', 'noteboards', 'shared', 'search', 'reminders', 'tags', 'canvas', 'sticky', 'landing', 'docs', 'favorites'];
      if (validTabs.includes(path)) {
        setActiveTab(path as any);
      } else {
        setActiveTab('landing');
      }
    };
    window.addEventListener('popstate', handleNavigation);
    window.addEventListener('hashchange', handleNavigation);
    return () => {
      window.removeEventListener('popstate', handleNavigation);
      window.removeEventListener('hashchange', handleNavigation);
    };
  }, []);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  const getPageDisplayName = () => {
    switch (activeTab) {
      case 'all_notes': return 'All Notes';
      case 'notebooks': return 'Notebooks';
      case 'noteboards': return 'Notecards';
      case 'shared': return 'Shared';
      case 'search': return 'Search';
      case 'reminders': return 'Reminders';
      case 'tags': return 'Tags';
      case 'canvas': return 'Canvas';
      case 'docs': return 'Docs';
      case 'favorites': return 'Favorites';
      default: return 'Home';
    }
  };

  // Dynamic document title update
  useEffect(() => {
    document.title = `Design Studio | ${getPageDisplayName()}`;
  }, [activeTab]);

  // Layout & UI Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState<SortOption>('updated-desc');
  const [viewLayout, setViewLayout] = useState<'grid' | 'list'>('grid');

  // Modals Toggles
  const [editingCard, setEditingCard] = useState<Partial<NoteCard> | null>(null);
  const [editorLayoutMode, setEditorLayoutMode] = useState<'popup' | 'full'>('popup');

  useEffect(() => {
    if (editingCard) {
      setEditorLayoutMode('popup');
    }
  }, [editingCard]);

  const toggleEditorFullscreen = () => {
    setEditorLayoutMode(prev => {
      const nextMode = prev === 'popup' ? 'full' : 'popup';
      if (nextMode === 'full') {
        setIsNotebooksSidebarCollapsed(true);
        setIsPagesSidebarCollapsed(true);
      } else {
        setIsNotebooksSidebarCollapsed(false);
        setIsPagesSidebarCollapsed(false);
      }
      return nextMode;
    });
  };

  const [showRecycleBin, setShowRecycleBin] = useState(false);
  const [isCreatingNotebook, setIsCreatingNotebook] = useState(false);

  // Custom states for lower left popovers
  const [showMorePopover, setShowMorePopover] = useState(false);
  const [remindersSubTab, setRemindersSubTab] = useState<'overdue' | 'upcoming'>('overdue');

  // Notebook Creation Form state
  const [newNotebookTitle, setNewNotebookTitle] = useState('');
  const [selectedPreset, setSelectedPreset] = useState(COVERS_PRESETS[0]);

  // Rename Notebook state
  const [renamingNotebookId, setRenamingNotebookId] = useState<string | null>(null);
  const [renameNotebookTitle, setRenameNotebookTitle] = useState('');

  // Active Alert alarm system
  const [triggeredAlarmCard, setTriggeredAlarmCard] = useState<NoteCard | null>(null);

  const [isSidebarMinimised, setIsSidebarMinimised] = useState(false);
  const [isNotebooksSidebarCollapsed, setIsNotebooksSidebarCollapsed] = useState(true);
  const [isPagesSidebarCollapsed, setIsPagesSidebarCollapsed] = useState(true);

  // Synchronize state attributes on document.documentElement for custom web component panels
  useEffect(() => {
    if (isSidebarMinimised) {
      document.documentElement.setAttribute('data-sidebar-minimised', 'true');
    } else {
      document.documentElement.removeAttribute('data-sidebar-minimised');
    }
    window.dispatchEvent(new CustomEvent('app-state-changed'));
  }, [isSidebarMinimised]);

  useEffect(() => {
    if (isHeaderVisible) {
      document.documentElement.setAttribute('data-header-visible', 'true');
    } else {
      document.documentElement.removeAttribute('data-header-visible');
    }
    window.dispatchEvent(new CustomEvent('app-state-changed'));
  }, [isHeaderVisible]);

  // Listen to custom toggle events from the web-components (Layout Settings dropdown)
  useEffect(() => {
    const handleToggleSidebar = () => {
      setIsSidebarMinimised(prev => {
        const nextVal = !prev;
        if (activeTab === 'canvas') {
          setIsHeaderVisible(!nextVal);
        }
        return nextVal;
      });
    };
    const handleToggleHeader = () => {
      setIsHeaderVisible(prev => !prev);
    };

    window.addEventListener('app-toggle-sidebar', handleToggleSidebar);
    window.addEventListener('app-toggle-header', handleToggleHeader);

    return () => {
      window.removeEventListener('app-toggle-sidebar', handleToggleSidebar);
      window.removeEventListener('app-toggle-header', handleToggleHeader);
    };
  }, [activeTab]);

  // Custom high-fidelity interactive state parameters matching screenshots exactly
  const [showPlusDropdown, setShowPlusDropdown] = useState(false);
  const [activeNotebookContextMenu, setActiveNotebookContextMenu] = useState<string | null>(null);
  const [infoNotebook, setInfoNotebook] = useState<Notebook | null>(null);
  const [lockNotebookId, setLockNotebookId] = useState<string | null>(null);
  const [lockPasscode, setLockPasscode] = useState('');
  const [lockError, setLockError] = useState('');
  const [unlockingId, setUnlockingId] = useState<string | null>(null);
  const [unlockPasscode, setUnlockPasscode] = useState('');
  const [unlockError, setUnlockingError] = useState('');
  const [changingCoverNotebookId, setChangingCoverNotebookId] = useState<string | null>(null);
  const [selectedChangeCoverPreset, setSelectedChangeCoverPreset] = useState(COVERS_PRESETS[0]);
  const [sharedFilter, setSharedFilter] = useState<'All' | 'Notes' | 'Notebooks' | 'Collections'>('All');

  const allNotesScrollRef = useRef<HTMLDivElement>(null);
  const [showAllNotesScrollButton, setShowAllNotesScrollButton] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (allNotesScrollRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = allNotesScrollRef.current;
        setShowAllNotesScrollButton(scrollHeight > clientHeight && scrollTop + clientHeight < scrollHeight - 20);
      }
    };
    const element = allNotesScrollRef.current;
    if (element) {
      element.addEventListener('scroll', handleScroll);
      // Initial check
      handleScroll();
    }
    return () => element?.removeEventListener('scroll', handleScroll);
  }, [activeCardsList]);

  // Load from local storage on mount
  useEffect(() => {
    const rawNotebooks = localStorage.getItem(LOCAL_STORAGE_NOTEBOOKS_KEY);
    const rawCards = localStorage.getItem(LOCAL_STORAGE_CARDS_KEY);
    const savedTheme = localStorage.getItem(LOCAL_STORAGE_DARK_MODE_KEY);

    let parsedBooks = DEFAULT_NOTEBOOKS;
    if (rawNotebooks) {
      parsedBooks = JSON.parse(rawNotebooks);
      let migrated = false;
      parsedBooks = parsedBooks.map(nb => {
        if (nb.title === 'My Notebook' || nb.title === 'My Notebooks') {
          migrated = true;
          return { ...nb, title: 'My Notecards' };
        }
        return nb;
      });
      setNotebooks(parsedBooks);
      if (migrated) {
        localStorage.setItem(LOCAL_STORAGE_NOTEBOOKS_KEY, JSON.stringify(parsedBooks));
      }
    } else {
      setNotebooks(DEFAULT_NOTEBOOKS);
      localStorage.setItem(LOCAL_STORAGE_NOTEBOOKS_KEY, JSON.stringify(DEFAULT_NOTEBOOKS));
    }

    if (rawCards) {
      setCards(JSON.parse(rawCards));
    } else {
      setCards(DEFAULT_NOTE_CARDS);
      localStorage.setItem(LOCAL_STORAGE_CARDS_KEY, JSON.stringify(DEFAULT_NOTE_CARDS));
    }

    // Default selection to first active notebook to give instantaneous clean multi-column layouts!
    const activeBooks = parsedBooks.filter((b) => !b.isDeleted);
    if (activeBooks.length > 0) {
      setSelectedNotebookId(activeBooks[0].id);
    }

    // Always load light theme as explicitly requested by user on initial load
    setIsDarkMode(false);
    document.documentElement.classList.remove('dark');
    document.documentElement.classList.remove('sl-theme-dark');
    document.documentElement.dataset.theme = 'light';
    localStorage.setItem('blocksuite:dark', 'false');
  }, []);

  // Sync back to local storage
  const syncNotebooks = (newBooks: Notebook[]) => {
    localStorage.setItem(LOCAL_STORAGE_NOTEBOOKS_KEY, JSON.stringify(newBooks));
  };

  const syncCards = (newCards: NoteCard[]) => {
    localStorage.setItem(LOCAL_STORAGE_CARDS_KEY, JSON.stringify(newCards));
  };

  // Dark mode toggler supporting BlockSuite variables and HTML classes
  const toggleDarkMode = () => {
    const nextDark = !isDarkMode;
    setIsDarkMode(nextDark);

    const html = document.querySelector('html');
    if (html) {
      if (nextDark) {
        html.classList.add('dark');
        html.classList.add('sl-theme-dark');
        html.dataset.theme = 'dark';
        localStorage.setItem('blocksuite:dark', 'true');
        localStorage.setItem(LOCAL_STORAGE_DARK_MODE_KEY, 'true');
      } else {
        html.classList.remove('dark');
        html.classList.remove('sl-theme-dark');
        html.dataset.theme = 'light';
        localStorage.setItem('blocksuite:dark', 'false');
        localStorage.setItem(LOCAL_STORAGE_DARK_MODE_KEY, 'false');
      }
    }
  };


  // Global Alarm Tick Checking daemon (60s timer interval check)
  useEffect(() => {
    const interval = setInterval(() => {
      const liveTimestamp = Date.now();
      const triggered = cards.find(
        (c) =>
          !c.isDeleted &&
          c.reminderTime &&
          c.reminderTime <= liveTimestamp &&
          c.reminderTime > liveTimestamp - 120000 // Only catch within last 2 minutes
      );
      if (triggered && triggeredAlarmCard?.id !== triggered.id) {
        setTriggeredAlarmCard(triggered);
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [cards, triggeredAlarmCard]);

  // Actions handlers
  const handleCreateNotebook = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNotebookTitle.trim()) return;

    const newBook: Notebook = {
      id: `book-${Date.now()}`,
      title: newNotebookTitle.trim(),
      coverStyle: {
        type: selectedPreset.type as 'gradient' | 'pattern' | 'solid' | 'image',
        primaryColor: selectedPreset.primaryColor,
        secondaryColor: selectedPreset.secondaryColor,
        patternName: selectedPreset.patternName,
        imageTheme: (selectedPreset as any).imageTheme
      },
      createdAt: Date.now()
    };

    const updatedBooks = [...notebooks, newBook];
    setNotebooks(updatedBooks);
    syncNotebooks(updatedBooks);
    setSelectedNotebookId(newBook.id);
    setIsCreatingNotebook(false);
    setNewNotebookTitle('');
  };

  const handleSelectNotebook = (id: string) => {
    const book = notebooks.find((b) => b.id === id);
    if (book?.isLocked) {
      setUnlockingId(id);
      setUnlockPasscode('');
      setUnlockingError('');
    } else {
      setSelectedNotebookId(id);
      setEditingCard(null);
    }
  };

  const handleRenameNotebook = (id: string, newTitle: string) => {
    if (!newTitle) return;
    const updated = notebooks.map((b) =>
      b.id === id ? { ...b, title: newTitle } : b
    );
    setNotebooks(updated);
    syncNotebooks(updated);
  };

  const handleDeleteNotebook = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (await confirm('Are you sure you want to move this entire notebook to archived?')) {
      const updated = notebooks.map((b) =>
        b.id === id ? { ...b, isDeleted: true, deletedAt: Date.now() } : b
      );
      setNotebooks(updated);
      syncNotebooks(updated);
      if (selectedNotebookId === id) {
        setSelectedNotebookId(null);
      }
    }
  };

  const handleCreateCard = (type: CardType) => {
    const defaultBookId = selectedNotebookId || notebooks.find(n => !n.isDeleted)?.id || 'book-1';
    const newCard: NoteCard = {
      id: `card-${Date.now()}`,
      notebookId: defaultBookId,
      type,
      title: '',
      colorClass: NOTE_COLORS[0].class, // Yellow
      createdAt: Date.now(),
      updatedAt: Date.now(),
      isFavorite: false,
      isLocked: false,
      tags: [],
    };

    if (type === 'checklist') {
      newCard.checklistContent = [
        { id: `task-${Date.now()}-1`, text: 'New custom task item', completed: false }
      ];
    } else if (type === 'text') {
      newCard.textContent = '';
    } else if (type === 'sketch') {
      newCard.sketchDataUrl = '';
    } else if (type === 'audio') {
      newCard.audioUrl = '';
      newCard.audioDuration = 0;
    }

    const updatedCards = [newCard, ...cards];
    setCards(updatedCards);
    syncCards(updatedCards);
    setEditingCard(newCard);
  };

  const saveSelectedCard = (updated: Partial<NoteCard>) => {
    const finalCards = cards.map((c) =>
      c.id === updated.id ? ({ ...c, ...updated, updatedAt: Date.now() } as NoteCard) : c
    );
    setCards(finalCards);
    syncCards(finalCards);
    setEditingCard(updated);
  };

  const handleUpdateCardTitle = (cardId: string, newTitle: string) => {
    const finalCards = cards.map((c) =>
      c.id === cardId ? ({ ...c, title: newTitle, textContent: stripHtml(newTitle), updatedAt: Date.now() } as NoteCard) : c
    );
    setCards(finalCards);
    syncCards(finalCards);
    if (editingCard && editingCard.id === cardId) {
      setEditingCard(prev => prev ? ({ ...prev, title: newTitle, textContent: stripHtml(newTitle) } as NoteCard) : null);
    }
  };

  const deleteCardToBin = (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const updated = cards.map((c) =>
      c.id === id ? { ...c, isDeleted: true, deletedAt: Date.now() } : c
    );
    setCards(updated);
    syncCards(updated);
    if (editingCard?.id === id) {
      setEditingCard(null);
    }
  };

  const duplicateCard = (card: NoteCard, e: React.MouseEvent) => {
    e.stopPropagation();
    const copied: NoteCard = {
      ...card,
      id: `card-${Date.now()}`,
      title: card.title ? `${card.title} (Copy)` : 'Copy',
      createdAt: Date.now(),
      updatedAt: Date.now(),
      isLocked: false,
    };
    const updated = [copied, ...cards];
    setCards(updated);
    syncCards(updated);
  };

  const toggleFavoriteValue = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = cards.map((c) =>
      c.id === id ? { ...c, isFavorite: !c.isFavorite } : c
    );
    setCards(updated);
    syncCards(updated);
    if (editingCard?.id === id) {
      setEditingCard((prev) => (prev ? { ...prev, isFavorite: !prev.isFavorite } : null));
    }
  };

  // Restores notebooks / cards from Trash Bin
  const handleRestoreNotebook = (id: string) => {
    const updated = notebooks.map((b) =>
      b.id === id ? { ...b, isDeleted: false, deletedAt: undefined } : b
    );
    setNotebooks(updated);
    syncNotebooks(updated);
  };

  const handleRestoreCard = (id: string) => {
    const updated = cards.map((c) =>
      c.id === id ? { ...c, isDeleted: false, deletedAt: undefined } : c
    );
    setCards(updated);
    syncCards(updated);
  };

  // Permanent Delete triggers
  const handlePermanentDeleteNotebook = async (id: string) => {
    if (await confirm('Permanently delete this notebook and all of its notes? This action is irreversible.')) {
      const updatedBooks = notebooks.filter((b) => b.id !== id);
      const updatedCards = cards.filter((c) => c.notebookId !== id);
      setNotebooks(updatedBooks);
      setCards(updatedCards);
      syncNotebooks(updatedBooks);
      syncCards(updatedCards);
    }
  };

  const handlePermanentDeleteCard = async (id: string) => {
    if (await confirm('Permanently delete this note card?')) {
      const updated = cards.filter((c) => c.id !== id);
      setCards(updated);
      syncCards(updated);
    }
  };

  // Filtering views computation
  // activeCardsList moved up

  // Counter metrics
  const notebookCardsCounter = activeNotebooksList.reduce<Record<string, number>>((acc, book) => {
    acc[book.id] = activeCardsList.filter((c) => c.notebookId === book.id).length;
    return acc;
  }, {});

  const allUniqueTags = Array.from(
    new Set(activeCardsList.flatMap((c) => c.tags || []))
  ).filter(Boolean);

  // Filter files based on Column 2 list context
  const getFilteredCards = () => {
    let result = [...activeCardsList];

    // Main context check
    if (activeTab === 'notebooks' && selectedNotebookId) {
      result = result.filter((c) => c.notebookId === selectedNotebookId);
    } else if (activeTab === 'favorites') {
      result = result.filter((c) => c.isFavorite);
    } else if (activeTab === 'tags' && selectedTag) {
      result = result.filter((c) => c.tags.includes(selectedTag));
    } else if (activeTab === 'reminders') {
      result = result.filter((c) => !!c.reminderTime);
    }

    // Global Search box checks
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(
        (c) =>
          c.title.toLowerCase().includes(query) ||
          c.textContent?.toLowerCase().includes(query) ||
          c.tags.some((t) => t.toLowerCase().includes(query))
      );
    }

    // Apply sorting selection options
    return result.sort((a, b) => {
      switch (sortOption) {
        case 'updated-asc':
          return a.updatedAt - b.updatedAt;
        case 'created-desc':
          return b.createdAt - a.createdAt;
        case 'title-asc':
          return a.title.localeCompare(b.title);
        case 'title-desc':
          return b.title.localeCompare(a.title);
        default:
          return b.updatedAt - a.updatedAt;
      }
    });
  };

  const filteredCards = getFilteredCards();
  const activeCard = (editingCard && editingCard.notebookId === selectedNotebookId)
    ? editingCard
    : (filteredCards.length > 0 ? filteredCards[0] : null);
  const currentNotebook = selectedNotebookId
    ? activeNotebooksList.find((b) => b.id === selectedNotebookId)
    : null;

  if (activeTab === 'landing') {
    return (
      <LandingPage
        onGetStarted={() => {
          setActiveTab('notebooks');
          const activeBooks = notebooks.filter((b) => !b.isDeleted);
          if (activeBooks.length > 0) {
            setSelectedNotebookId(activeBooks[0].id);
          }
        }}
        onOpenCanvas={() => {
          setActiveTab('canvas');
        }}
        onOpenSticky={() => {
          setActiveTab('noteboards');
        }}
      />
    );
  }

  const renderSettingsDropdown = (isFloating = false) => {
    return (
      <div 
        className="absolute z-[100] w-56 bg-white dark:bg-slate-900 border border-slate-205 dark:border-slate-800 rounded-xl shadow-lg p-2.5 animate-in fade-in zoom-in-95 duration-150 text-slate-800 dark:text-slate-100"
        style={isFloating ? { right: '0px', top: '44px' } : { right: '48px', top: '50px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[10px] uppercase font-bold text-slate-400 dark:text-slate-500 px-2 py-1 tracking-wider border-b border-slate-100 dark:border-slate-800 mb-1.5 flex justify-between items-center">
          <span>Settings</span>
          {isFloating && (
            <button 
              onClick={() => setShowSettingsDropdown(false)} 
              className="text-xs hover:text-slate-700 dark:hover:text-slate-350 cursor-pointer font-bold border-none bg-transparent text-slate-400"
            >
              ✕
            </button>
          )}
        </div>
        
        {/* Header Visibility Menu Item with Sub-menu */}
        <div className="relative">
          <button
            onMouseEnter={() => setShowSettingsSubMenu(true)}
            onClick={() => setShowSettingsSubMenu(prev => !prev)}
            className="w-full flex items-center justify-between text-xs font-semibold py-2 px-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-zinc-300 cursor-pointer transition-colors border-none bg-transparent text-left"
          >
            <span className="flex items-center gap-2">
              <Settings2 className="w-3.5 h-3.5" /> Header Visibility
            </span>
            <ChevronLeft className="w-3.5 h-3.5 text-slate-400" />
          </button>

          {/* Sub-menu on Hover/Click Left */}
          {showSettingsSubMenu && (
            <div 
              onMouseLeave={() => setShowSettingsSubMenu(false)}
              className="absolute right-[225px] top-0 z-[101] w-40 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-lg p-2 animate-in fade-in slide-in-from-right-2 duration-150"
            >
              <div className="text-[9px] uppercase font-bold text-slate-400 dark:text-slate-500 px-1.5 py-0.5 tracking-wider border-b border-slate-100 dark:border-slate-800 mb-1">
                Header
              </div>
              <button
                onClick={() => {
                  setIsHeaderVisible(true);
                  showToast("Notebooks header is now shown.", "success");
                  setShowSettingsSubMenu(false);
                  setShowSettingsDropdown(false);
                }}
                className={`w-full flex items-center justify-between text-xs font-semibold py-1.5 px-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors border-none bg-transparent text-left ${
                  isHeaderVisible ? 'text-blue-600 dark:text-blue-400' : 'text-slate-700 dark:text-zinc-300'
                }`}
              >
                <span>Allow</span>
                {isHeaderVisible && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
              </button>
              <button
                onClick={() => {
                  setIsHeaderVisible(false);
                  showToast("Header hidden. Use the floating gear icon to restore it.", "info");
                  setShowSettingsSubMenu(false);
                  setShowSettingsDropdown(false);
                }}
                className={`w-full flex items-center justify-between text-xs font-semibold py-1.5 px-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors border-none bg-transparent text-left ${
                  !isHeaderVisible ? 'text-blue-600 dark:text-blue-400' : 'text-slate-700 dark:text-zinc-300'
                }`}
              >
                <span>Disable</span>
                {!isHeaderVisible && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
              </button>
            </div>
          )}
        </div>

        {/* Other dummy/informational options for design quality */}
        <button
          onClick={() => showToast("Security Passcode parameters configured.", "info")}
          className="w-full flex items-center gap-2 text-xs font-semibold py-2 px-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-zinc-300 cursor-pointer transition-colors border-none bg-transparent text-left"
        >
          <Lock className="w-3.5 h-3.5" /> Passcode Lock
        </button>
        <button
          onClick={() => showToast("Cover presets customization opened.", "info")}
          className="w-full flex items-center gap-2 text-xs font-semibold py-2 px-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-zinc-300 cursor-pointer transition-colors border-none bg-transparent text-left"
        >
          <BookOpen className="w-3.5 h-3.5" /> Cover Presets
        </button>
      </div>
    );
  };

  return (
    <div className="h-screen bg-[#fafbfc] dark:bg-slate-950 flex flex-col font-sans text-slate-800 dark:text-slate-100 transition-colors duration-200 overflow-hidden">

      {/* 1. TOP HEADER BRAND (SLIGHTLY RE-STYLED BRAND WITHOUT COMPANY LABELS) */}
      {activeTab !== 'landing' && (activeTab !== 'canvas' || isHeaderVisible) && (
        <header className="bg-white dark:bg-[#111317] border-b border-[#e9ebed] dark:border-slate-900 z-30 px-6 py-2.5 flex items-center justify-between h-14 shrink-0 shadow-xs">
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                const nextVal = !isSidebarMinimised;
                setIsSidebarMinimised(nextVal);
                if (activeTab === 'canvas') {
                  setIsHeaderVisible(!nextVal);
                }
              }}
              className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-900 rounded-lg text-slate-500 cursor-pointer mr-2 flex items-center justify-center shrink-0"
              title={isSidebarMinimised ? "Expand Sidebar Menu" : "Minimize Sidebar Menu"}
            >
              <Columns className={`w-4.5 h-4.5 transition-transform ${isSidebarMinimised ? 'rotate-180' : ''}`} />
            </button>

            {activeTab === 'canvas' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsHeaderVisible(false);
                  showToast("Header hidden. Use the floating gear icon to restore it.", 'info');
                }}
                className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-900 rounded-lg text-slate-500 hover:text-slate-800 dark:hover:text-white cursor-pointer mr-2 flex items-center justify-center shrink-0"
                title="Hide Header"
              >
                <Maximize className="w-4 h-4" />
              </button>
            )}

            <div
              className="flex items-center gap-1.5 cursor-pointer select-none"
              onClick={() => {
                setEditingCard(null);
                setActiveTab('landing');
              }}
            >
              {/* Beautiful generic multiclass sheets stacked logo graphic replacing icon */}
              <div className="relative w-6 h-6 flex items-center justify-center mr-1">
                <div className="absolute top-[3px] left-[5px] w-[13px] h-[16px] rounded-[2px] bg-[#4285f4] rotate-[10deg] opacity-80 shadow-xs" />
                <div className="absolute top-[2px] left-[3px] w-[13px] h-[16px] rounded-[2px] bg-[#ea4335] rotate-[-5deg] opacity-90 shadow-xs" />
                <div className="absolute top-[1px] left-[1px] w-[13px] h-[16px] rounded-[2px] bg-[#fbbc05] shadow-xs" />
              </div>
              <div>
                <span className="text-[17px] font-bold tracking-tight text-slate-900 dark:text-white font-sans">
                  Design Studio <span className="text-slate-400 dark:text-slate-500 font-medium">| {getPageDisplayName()}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Header Global Quick Filter text input */}
          <div className="hidden md:flex items-center bg-slate-100 dark:bg-slate-900 px-4 py-1.5 rounded-full w-full max-w-xs border border-transparent focus-within:border-slate-300 dark:focus-within:border-slate-800 transition-all">
            <Search className="w-3.5 h-3.5 text-slate-400 mr-2 shrink-0" />
            <input
              type="text"
              placeholder="Search stickies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent text-xs w-full outline-none text-slate-700 dark:text-white"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="p-0.5 text-slate-400 hover:text-slate-600 font-extrabold text-xs cursor-pointer">
                ×
              </button>
            )}
          </div>

          <div className="flex items-center gap-4">
            {/* Zia-style dynamic assistant spark icon */}
            <div
              onClick={() => showToast("Zia Intelligent Assistant is active. Type '@zia' in text notes or record audio cards to query notebook metadata!", 'info')}
              className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-900 rounded-full transition-all cursor-pointer flex items-center justify-center relative group"
              title="Zia Assistant"
            >
              <div className="absolute -inset-1 bg-gradient-to-r from-amber-400 via-rose-500 to-sky-500 rounded-full opacity-0 group-hover:opacity-15 blur-sm transition-opacity" />
              <svg className="w-6 h-6" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Zia multi-colored star sparkles representation */}
                <path d="M18 4L20.8 11.2L28 14L20.8 16.8L18 24L15.2 16.8L8 14L15.2 11.2L18 4Z" fill="url(#ziaStarGradient)" />
                <circle cx="12" cy="24" r="2" fill="#3498db" />
                <circle cx="24" cy="8" r="2.5" fill="#f1c40f" />
                <defs>
                  <linearGradient id="ziaStarGradient" x1="8" y1="4" x2="28" y2="24" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#f39c12" />
                    <stop offset="50%" stopColor="#e74c3c" />
                    <stop offset="100%" stopColor="#2ecc71" />
                  </linearGradient>
                </defs>
              </svg>
            </div>

            {/* Plus Add with Black dropdown matching screenshot 1 */}
            <div className="relative">
              <button
                onClick={() => setShowPlusDropdown(!showPlusDropdown)}
                className="w-[36px] h-[36px] rounded-lg bg-black text-white hover:bg-neutral-900 flex items-center justify-center font-bold text-xl select-none transition-all active:scale-95 cursor-pointer shadow-md"
                title="Add Note or Book"
                id="header-create-notebook-btn"
              >
                +
              </button>

              <AnimatePresence>
                {showPlusDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-40 bg-transparent"
                      onClick={() => setShowPlusDropdown(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                      className="absolute right-0 mt-2.5 w-[252px] bg-[#0c0d12] text-white rounded-xl shadow-2xl p-4.5 z-50 border border-neutral-800/60 flex flex-col gap-4 select-none"
                    >
                      {/* The Grid (3 Columns) for Note Types */}
                      <div className="grid grid-cols-3 gap-y-4 gap-x-1.5 text-center">
                        {/* Write */}
                        <button
                          onClick={() => {
                            handleCreateCard('text');
                            setShowPlusDropdown(false);
                          }}
                          className="flex flex-col items-center justify-center gap-1 group cursor-pointer border-none outline-none focus:outline-none"
                        >
                          <div className="w-[42px] h-[42px] rounded-full flex items-center justify-center bg-neutral-900 group-hover:bg-neutral-800 text-zinc-105 transition-colors">
                            <svg className="w-5 h-5 text-zinc-200 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" strokeWidth="2.1" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                            </svg>
                          </div>
                          <span className="text-[10px] font-medium tracking-wide text-zinc-300">Write</span>
                        </button>

                        {/* Capture */}
                        <button
                          onClick={() => {
                            handleCreateCard('photo');
                            setShowPlusDropdown(false);
                          }}
                          className="flex flex-col items-center justify-center gap-1 group cursor-pointer border-none outline-none focus:outline-none"
                        >
                          <div className="w-[42px] h-[42px] rounded-full flex items-center justify-center bg-neutral-900 group-hover:bg-neutral-800 text-zinc-105 transition-colors">
                            <svg className="w-5 h-5 text-zinc-200 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" strokeWidth="2.1" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
                              <circle cx="12" cy="13" r="3.25" stroke="currentColor" strokeWidth="2.1" />
                            </svg>
                          </div>
                          <span className="text-[10px] font-medium tracking-wide text-zinc-300">Capture</span>
                        </button>

                        {/* To Do */}
                        <button
                          onClick={() => {
                            handleCreateCard('checklist');
                            setShowPlusDropdown(false);
                          }}
                          className="flex flex-col items-center justify-center gap-1 group cursor-pointer border-none outline-none focus:outline-none"
                        >
                          <div className="w-[42px] h-[42px] rounded-full flex items-center justify-center bg-neutral-900 group-hover:bg-neutral-800 text-zinc-105 transition-colors">
                            <svg className="w-5 h-5 text-zinc-200 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" strokeWidth="2.1" viewBox="0 0 24 24">
                              <rect x="3.75" y="3.75" width="16.5" height="16.5" rx="2.5" stroke="currentColor" strokeWidth="2.1" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 12l2.75 2.75L16 9" />
                            </svg>
                          </div>
                          <span className="text-[10px] font-medium tracking-wide text-zinc-300">To Do</span>
                        </button>

                        {/* Attach */}
                        <button
                          onClick={() => {
                            const fileInput = document.createElement('input');
                            fileInput.type = 'file';
                            fileInput.onchange = () => {
                              handleCreateCard('photo');
                            };
                            fileInput.click();
                            setShowPlusDropdown(false);
                          }}
                          className="flex flex-col items-center justify-center gap-1 group cursor-pointer border-none outline-none focus:outline-none"
                        >
                          <div className="w-[42px] h-[42px] rounded-full flex items-center justify-center bg-neutral-900 group-hover:bg-neutral-800 text-zinc-105 transition-colors">
                            <svg className="w-5 h-5 text-zinc-200 transform rotate-45 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" strokeWidth="2.1" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                            </svg>
                          </div>
                          <span className="text-[10px] font-medium tracking-wide text-zinc-300">Attach</span>
                        </button>

                        {/* Draw */}
                        <button
                          onClick={() => {
                            handleCreateCard('sketch');
                            setShowPlusDropdown(false);
                          }}
                          className="flex flex-col items-center justify-center gap-1 group cursor-pointer border-none outline-none focus:outline-none"
                        >
                          <div className="w-[42px] h-[42px] rounded-full flex items-center justify-center bg-neutral-900 group-hover:bg-neutral-800 text-zinc-105 transition-colors">
                            <svg className="w-5 h-5 text-zinc-200 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" strokeWidth="2.1" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 21l5.096-.813M9.813 15.904L19.5 6.218M9.813 15.904L5.5 11.5M19.5 6.218a2.5 2.5 0 113.536 3.536L13.344 19.44M19.5 6.218l-3.5 3.5m0 0L11.5 5.5" />
                            </svg>
                          </div>
                          <span className="text-[10px] font-medium tracking-wide text-zinc-300">Draw</span>
                        </button>

                        {/* Audio */}
                        <button
                          onClick={() => {
                            handleCreateCard('audio');
                            setShowPlusDropdown(false);
                          }}
                          className="flex flex-col items-center justify-center gap-1 group cursor-pointer border-none outline-none focus:outline-none"
                        >
                          <div className="w-[42px] h-[42px] rounded-full flex items-center justify-center bg-neutral-900 group-hover:bg-neutral-800 text-zinc-105 transition-colors">
                            <svg className="w-5 h-5 text-zinc-200 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" strokeWidth="2.1" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
                            </svg>
                          </div>
                          <span className="text-[10px] font-medium tracking-wide text-zinc-300">Audio</span>
                        </button>

                        {/* Video */}
                        <button
                          onClick={() => {
                            showToast("Initializing premium HD video canvas recording... Camera active.", 'info');
                            handleCreateCard('photo');
                            setShowPlusDropdown(false);
                          }}
                          className="flex flex-col items-center justify-center gap-1 group cursor-pointer border-none outline-none focus:outline-none"
                        >
                          <div className="w-[42px] h-[42px] rounded-full flex items-center justify-center bg-neutral-900 group-hover:bg-neutral-800 text-zinc-105 transition-colors">
                            <svg className="w-5 h-5 text-zinc-200 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" strokeWidth="2.1" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <span className="text-[10px] font-medium tracking-wide text-zinc-300">Video</span>
                        </button>
                      </div>

                      {/* Divider line matches Screenshot 1 */}
                      <div className="border-t border-neutral-800/80 my-1.5" />

                      {/* Lower segment list */}
                      <div className="flex flex-col gap-1">
                        {/* Notebook */}
                        <button
                          onClick={() => {
                            setIsCreatingNotebook(true);
                            setShowPlusDropdown(false);
                          }}
                          className="w-full flex items-center gap-3.5 px-3 py-2.5 hover:bg-neutral-900 hover:text-white rounded-lg text-zinc-300 transition-colors cursor-pointer text-left focus:outline-none text-[12px] font-semibold"
                        >
                          <svg className="w-4.5 h-4.5 text-zinc-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                          </svg>
                          <span>Notebook</span>
                        </button>

                        {/* Noteboard (Commented out)
                        <button
                          onClick={() => {
                            setActiveTab('noteboards');
                            setShowPlusDropdown(false);
                          }}
                          className="w-full flex items-center gap-3.5 px-3 py-2.5 hover:bg-neutral-900 hover:text-white rounded-lg text-zinc-300 transition-colors cursor-pointer text-left focus:outline-none text-[12px] font-semibold"
                        >
                          <svg className="w-4.5 h-4.5 text-zinc-405" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                            <line x1="9" y1="3" x2="9" y2="21" stroke="currentColor" strokeWidth="2" />
                            <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2" />
                          </svg>
                          <span>NoteCard</span>
                        </button>
                        */}
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>

            {/* Settings gear precisement */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSettingsDropdown(prev => !prev);
                }}
                className="p-1.5 text-slate-500 hover:text-slate-850 dark:text-slate-400 dark:hover:text-white transition-all cursor-pointer rounded-full hover:bg-slate-100 dark:hover:bg-slate-900"
                title="Notebook Settings"
              >
                <Settings className="w-5 h-5 shrink-0" />
              </button>
              {showSettingsDropdown && renderSettingsDropdown(false)}
            </div>

            {/* Profile disk design */}
            <div
              className="w-[32px] h-[32px] rounded-full border border-orange-200 shadow-xs cursor-pointer select-none overflow-hidden shrink-0 flex items-center justify-center hover:scale-105 transition-transform"
              style={{ background: 'radial-gradient(circle, #fbd249 0%, #c19525 100%)' }}
              title="Profile details"
            >
              <User className="w-4.5 h-4.5 text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.3)]" />
            </div>
          </div>
        </header>
      )}
      {/* 2. CORE WORKSPACE: MULTI-COLUMN DESIGN MATCHING SCREENSHOTS 1-8 */}
      <div className="flex-1 flex overflow-hidden relative" id="main-application-body">

        {/* Floating Expand Buttons */}
        {isNotebooksSidebarCollapsed && activeTab === 'notebooks' && (
          <button
            onClick={() => setIsNotebooksSidebarCollapsed(false)}
            className="absolute z-50 p-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-md hover:bg-slate-50 dark:hover:bg-slate-750 cursor-pointer flex items-center justify-center transition-all duration-200 animate-in fade-in"
            style={{
              left: `${(isSidebarMinimised ? 0 : 76) + 16}px`,
              top: '16px'
            }}
            title="Expand Notebooks Sidebar"
          >
            <ChevronRight className="w-4 h-4 text-slate-600 dark:text-slate-350" />
          </button>
        )}

        {isPagesSidebarCollapsed && activeTab === 'notebooks' && selectedNotebookId && (
          <button
            onClick={() => setIsPagesSidebarCollapsed(false)}
            className="absolute z-50 p-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-md hover:bg-slate-50 dark:hover:bg-slate-750 cursor-pointer flex items-center justify-center transition-all duration-200 animate-in fade-in"
            style={{
              left: `${(isSidebarMinimised ? 0 : 76) + (isNotebooksSidebarCollapsed ? 60 : 296)}px`,
              top: '16px'
            }}
            title="Expand Notes Sidebar"
          >
            <ChevronRight className="w-4 h-4 text-slate-600 dark:text-slate-350" />
          </button>
        )}

        {/* COLUMN 1: VERTICAL LIGHT SLIM SIDEBAR (FAR LEFT) */}
        <aside className={`${isSidebarMinimised ? 'w-0 opacity-0 overflow-hidden' : 'w-[76px] opacity-100'} transition-all duration-300 shrink-0 bg-[#fafbfc] dark:bg-[#111317] flex flex-col justify-between py-6 border-r border-[#e8eaed] dark:border-slate-900 select-none z-[500]`}>

          <div className="flex-1 flex flex-col gap-4 px-1.5 overflow-y-auto overflow-x-hidden no-scrollbar">
            {/* All Notes Button */}
            <button
              onClick={() => {
                setEditingCard(null);
                setActiveTab('all_notes');
              }}
              className={`w-[66px] h-[58px] flex flex-col items-center justify-center gap-1 rounded-xl transition-all cursor-pointer select-none text-center ${activeTab === 'all_notes'
                ? 'bg-[#e8ebed] dark:bg-slate-800 text-slate-950 dark:text-white font-semibold shadow-xs'
                : 'text-slate-500 hover:bg-slate-100/80 hover:text-slate-800 dark:hover:bg-slate-900'
                }`}
            >
              <FileText className="w-4.5 h-4.5 stroke-[1.8]" />
              <span className="text-[9.5px] font-sans font-medium tracking-wide">All Notes</span>
            </button>

            {/* Notebooks Button */}
            <button
              onClick={() => {
                setEditingCard(null);
                setActiveTab('notebooks');
              }}
              className={`w-[66px] h-[58px] flex flex-col items-center justify-center gap-1 rounded-xl transition-all cursor-pointer select-none text-center ${activeTab === 'notebooks'
                ? 'bg-[#e8ebed] dark:bg-slate-800 text-slate-950 dark:text-white font-semibold shadow-xs'
                : 'text-slate-500 hover:bg-slate-100/80 hover:text-slate-800 dark:hover:bg-slate-900'
                }`}
            >
              <BookOpen className="w-4.5 h-4.5 stroke-[1.8]" />
              <span className="text-[9.5px] font-sans font-medium tracking-wide">Notebooks</span>
            </button>

            {/* Noteboards Button (Commented out)
            <button
              onClick={() => {
                setEditingCard(null);
                setActiveTab('noteboards');
              }}
              className={`w-[66px] h-[58px] flex flex-col items-center justify-center gap-1 rounded-xl transition-all cursor-pointer select-none text-center ${activeTab === 'noteboards'
                  ? 'bg-[#e8ebed] dark:bg-slate-800 text-slate-950 dark:text-white font-semibold shadow-xs'
                  : 'text-slate-500 hover:bg-slate-100/80 hover:text-slate-800 dark:hover:bg-slate-900'
                }`}
            >
              <Grid className="w-4.5 h-4.5 stroke-[1.8]" />
              <span className="text-[9.5px] font-sans font-medium tracking-wide">Notecards</span>
            </button>
            */}

            {/* Shared Button */}
            <button
              onClick={() => {
                setEditingCard(null);
                setActiveTab('shared');
              }}
              className={`w-[66px] h-[58px] flex flex-col items-center justify-center gap-1 rounded-xl transition-all cursor-pointer select-none text-center ${activeTab === 'shared'
                ? 'bg-[#e8ebed] dark:bg-slate-800 text-slate-950 dark:text-white font-semibold shadow-xs'
                : 'text-slate-500 hover:bg-slate-100/80 hover:text-slate-800 dark:hover:bg-slate-900'
                }`}
            >
              <Users className="w-4.5 h-4.5 stroke-[1.8]" />
              <span className="text-[9.5px] font-sans font-medium tracking-wide">Shared</span>
            </button>

            {/* Docs Button */}
            <button
              onClick={() => {
                setEditingCard(null);
                setActiveTab('docs');
              }}
              className={`w-[66px] h-[58px] flex flex-col items-center justify-center gap-1 rounded-xl transition-all cursor-pointer select-none text-center ${activeTab === 'docs'
                ? 'bg-[#e8ebed] dark:bg-slate-800 text-slate-950 dark:text-white font-semibold shadow-xs'
                : 'text-slate-500 hover:bg-slate-100/80 hover:text-slate-800 dark:hover:bg-slate-900'
                }`}
            >
              <Edit3 className="w-4.5 h-4.5 stroke-[1.8]" />
              <span className="text-[9.5px] font-sans font-medium tracking-wide">Docs</span>
            </button>

            {/* Canvas Button */}
            <button
              onClick={() => {
                setEditingCard(null);
                setActiveTab('canvas');
              }}
              className={`w-[66px] h-[58px] flex flex-col items-center justify-center gap-1 rounded-xl transition-all cursor-pointer select-none text-center ${activeTab === 'canvas'
                ? 'bg-[#e8ebed] dark:bg-slate-800 text-slate-950 dark:text-white font-semibold shadow-xs'
                : 'text-slate-500 hover:bg-slate-100/80 hover:text-slate-800 dark:hover:bg-slate-900'
                }`}
            >
              <Paintbrush className="w-4.5 h-4.5 stroke-[1.8]" />
              <span className="text-[9.5px] font-sans font-medium tracking-wide">Canvas</span>
            </button>

            {/* Partition Divider line */}
            <div className="my-2 px-4 w-full">
              <div className="border-t border-[#e8eaed] dark:border-slate-800" />
            </div>

            {/* Sub-stack B: Icon-Only Controls */}
            <div className="flex flex-col items-center gap-4 px-2">
              {/* Search Button */}
              <button
                onClick={() => {
                  setEditingCard(null);
                  setActiveTab('search');
                }}
                className={`w-[40px] h-[40px] flex items-center justify-center rounded-lg transition-all cursor-pointer ${activeTab === 'search'
                  ? 'bg-[#e8ebed] dark:bg-slate-850 text-slate-950 dark:text-white stroke-[2.2]'
                  : 'text-slate-500 hover:bg-slate-100/80 hover:text-slate-800 dark:hover:bg-slate-900'
                  }`}
                title="Search panel"
              >
                <Search className="w-4.5 h-4.5" />
              </button>

              {/* Reminders Button */}
              <button
                onClick={() => {
                  setEditingCard(null);
                  setActiveTab('reminders');
                }}
                className={`w-[40px] h-[40px] flex items-center justify-center rounded-lg transition-all cursor-pointer ${activeTab === 'reminders'
                  ? 'bg-[#e8ebed] dark:bg-slate-850 text-slate-950 dark:text-white stroke-[2.2]'
                  : 'text-slate-500 hover:bg-slate-100/80 hover:text-slate-800 dark:hover:bg-slate-900'
                  }`}
                title="Reminders clock"
              >
                <Clock className="w-4.5 h-4.5" />
              </button>

              {/* Tags Button */}
              <button
                onClick={() => {
                  setEditingCard(null);
                  setActiveTab('tags');
                }}
                className={`w-[40px] h-[40px] flex items-center justify-center rounded-lg transition-all cursor-pointer ${activeTab === 'tags'
                  ? 'bg-[#e8ebed] dark:bg-slate-850 text-slate-950 dark:text-white stroke-[2.2]'
                  : 'text-slate-500 hover:bg-slate-100/80 hover:text-slate-800 dark:hover:bg-slate-900'
                  }`}
                title="Tags folder"
              >
                <Tag className="w-4.5 h-4.5" />
              </button>

              {/* Trash Button */}
              <button
                onClick={() => setShowRecycleBin(true)}
                className="w-[40px] h-[40px] flex items-center justify-center rounded-lg transition-all cursor-pointer text-slate-500 hover:bg-slate-100/80 hover:text-rose-600 dark:hover:bg-slate-900"
                title="Trash"
              >
                <Trash2 className="w-4.5 h-4.5" />
              </button>

              {/* Theme Toggle Button */}
              <button
                onClick={toggleDarkMode}
                className="w-[40px] h-[40px] flex items-center justify-center rounded-lg transition-all cursor-pointer text-slate-500 hover:bg-slate-100/80 hover:text-slate-800 dark:hover:bg-slate-900"
                title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                {isDarkMode ? <Sun className="w-4.5 h-4.5" /> : <Moon className="w-4.5 h-4.5" />}
              </button>
            </div>
          </div>
        </aside>

        {/* COLUMN 2: INNER SELECTION LIST PANEL (DYNAMIC SIDEBAR COLUMN) */}
        <AnimatePresence mode="wait">
          {activeTab !== 'noteboards' && activeTab !== 'canvas' && activeTab !== 'docs' && activeTab !== 'sticky' && activeTab !== 'landing' && (
            <motion.aside
              key={activeTab}
              initial={{ width: 0, opacity: 0 }}
              animate={{
                width: isNotebooksSidebarCollapsed ? 0 : 280,
                opacity: isNotebooksSidebarCollapsed ? 0 : 1,
              }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className={`shrink-0 bg-[#f5f6f8] dark:bg-[#121417] flex flex-col h-full overflow-hidden select-none z-10 ${isNotebooksSidebarCollapsed ? 'border-r-0' : 'border-r border-[#e8eaed] dark:border-slate-900/80'}`}
            >

              {/* 2A. Conditionally Rendered Header & Body of Column 2 list based on Active tab */}
              {activeTab === 'all_notes' && (
                <div className="flex flex-col h-full overflow-hidden">
                  <div className="p-4 flex items-center justify-between border-b border-[#e9ebed] dark:border-slate-900 bg-[#f5f6f8] dark:bg-[#121417]">
                    <h2 className="text-[15px] font-bold text-slate-800 dark:text-white font-sans">All Notes</h2>
                    <div className="flex items-center gap-2 text-slate-400">
                      <button onClick={() => showToast("Notes favorited or tagged. Enjoy using Notebook!", 'success')} className="p-1 hover:text-slate-800 dark:hover:text-white cursor-pointer"><Heart className="w-4 h-4" /></button>
                      <button className="p-1 hover:text-slate-800 dark:hover:text-white cursor-pointer"><Search className="w-4 h-4" /></button>
                      <button className="p-1 hover:text-slate-800 dark:hover:text-white cursor-pointer"><MoreHorizontal className="w-4 h-4" /></button>
                    </div>
                  </div>

                  {/* Flat scrolling card items in all_notes selection sidebar */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3 relative bg-[#f5f6f8] dark:bg-transparent" ref={allNotesScrollRef}>
                    {activeCardsList.map((card) => {
                      const isSelected = editingCard?.id === card.id;
                      const hasTheme = card.deskThemeIdx !== undefined;
                      return (
                        <div
                          key={card.id}
                          onClick={() => setEditingCard(card)}
                          className={`p-3.5 rounded-lg border cursor-pointer relative flex flex-col justify-between h-[105px] transition-all select-none ${isSelected
                            ? 'ring-2 ring-indigo-500 border-transparent shadow-md scale-[1.02]'
                            : hasTheme
                              ? 'hover:translate-y-[-1px] hover:shadow-xs'
                              : 'hover:translate-y-[-1px] hover:shadow-xs border-amber-300/40 bg-amber-50/70 dark:bg-amber-950/20'
                            } ${hasTheme ? '' : card.colorClass}`}
                          style={{
                            backgroundColor: hasTheme ? DESK_THEMES[card.deskThemeIdx!].bgValue : undefined,
                            borderColor: isSelected
                              ? 'transparent'
                              : (hasTheme ? DESK_THEMES[card.deskThemeIdx!].accentBorder : undefined),
                            color: hasTheme ? DESK_THEMES[card.deskThemeIdx!].textValue : undefined,
                          }}
                        >
                          <div className="flex-1 min-w-0">
                            <h4 className="font-bold text-xs text-slate-900 dark:text-slate-100 truncate line-clamp-1 leading-snug">
                              {card.title || 'Untitled Note'}
                            </h4>
                            <p className="text-[11px] text-slate-600 dark:text-slate-400 font-medium mt-1 leading-normal line-clamp-2 truncate overflow-hidden">
                              {stripHtml(card.textContent) || (card.type === 'checklist' ? 'Checklist values configured' : 'Note details representation')}
                            </p>
                          </div>
                          <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-black/[0.04] text-[9.5px] font-mono text-slate-400 select-none">
                            <span>Today</span>
                            {card.isFavorite && <Heart className="w-2.5 h-2.5 text-rose-500 fill-rose-500" />}
                          </div>
                        </div>
                      );
                    })}
                    {activeCardsList.length === 0 && (
                      <div className="text-center py-12 text-xs text-slate-400 font-medium italic">
                        No notes available
                      </div>
                    )}
                    {showAllNotesScrollButton && (
                      <button
                        onClick={() => allNotesScrollRef.current?.scrollTo({ top: allNotesScrollRef.current.scrollHeight, behavior: 'smooth' })}
                        className="absolute bottom-4 right-4 bg-white/90 dark:bg-slate-800/90 p-2 rounded-full shadow-lg border border-slate-200 dark:border-slate-700 cursor-pointer z-50 hover:bg-white dark:hover:bg-slate-700 transition"
                      >
                        <ChevronDown className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'notebooks' && (
                <div className="flex flex-col h-full overflow-hidden">
                  <div className="p-4 flex items-center justify-between border-b border-[#e9ebed] dark:border-slate-900 bg-[#f5f6f8] dark:bg-[#121417]">
                    <div className="flex items-center gap-2">
                      <div>
                        <h2 className="text-[15px] font-bold text-slate-800 dark:text-white font-sans">Notebooks</h2>
                        <span className="text-[11px] font-medium text-slate-400 mt-0.5 block">{activeNotebooksList.length} Books</span>
                      </div>
                      <button
                        onClick={() => setIsNotebooksSidebarCollapsed(true)}
                        className="p-1 hover:bg-slate-200 dark:hover:bg-slate-850 rounded-md text-slate-500 hover:text-slate-850 dark:hover:text-white cursor-pointer"
                        title="Minimize Sidebar"
                      >
                        <ChevronLeft className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-400">
                      <button className="p-1 hover:text-slate-800 dark:hover:text-white cursor-pointer"><Search className="w-4 h-4" /></button>
                      <button className="p-1 hover:text-slate-800 dark:hover:text-white cursor-pointer"><MoreHorizontal className="w-4 h-4" /></button>
                      <button
                        onClick={() => setIsCreatingNotebook(true)}
                        className="p-1 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-md text-slate-700 dark:text-white cursor-pointer font-bold shrink-0"
                      >
                        <Plus className="w-4.5 h-4.5 stroke-[2.5]" />
                      </button>
                    </div>
                  </div>

                  {/* Notebook spine layout selection list */}
                  <div className="flex-1 overflow-y-auto p-2 py-3 space-y-2.5 bg-[#f5f6f8] dark:bg-transparent auto-scrollbar">
                    {activeNotebooksList.map((nb) => {
                      const count = notebookCardsCounter[nb.id] || 0;
                      const isSelected = selectedNotebookId === nb.id;

                      return (
                        <div
                          key={nb.id}
                          onClick={() => handleSelectNotebook(nb.id)}
                          className={`group flex items-center gap-3 p-2 px-2.5 rounded-lg cursor-pointer transition-all select-none border border-transparent relative ${isSelected
                            ? 'bg-white dark:bg-slate-800 border-[#e3e5e8] dark:border-slate-700 shadow-sm'
                            : 'hover:bg-slate-200/50 dark:hover:bg-slate-900'
                            }`}
                        >
                          {/* Spine style miniature notebook cover */}
                          <NotebookCover title={nb.title} coverStyle={nb.coverStyle} size="sm" />

                          {/* Title details */}
                          <div className="min-w-0 flex-1">
                            <span className="text-xs font-bold text-slate-800 dark:text-slate-250 block truncate leading-snug">
                              {nb.title}
                            </span>
                            <span className="text-[10px] text-slate-400 font-medium">
                              {count} note{count === 1 ? '' : 's'}
                            </span>
                          </div>

                          {/* Interactive Options button toggling our rich context menu */}
                          <div className="absolute right-2 opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity z-20 bg-inherit pr-0.5">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveNotebookContextMenu(activeNotebookContextMenu === nb.id ? null : nb.id);
                              }}
                              className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-500 hover:text-slate-900 rounded-md shrink-0 cursor-pointer"
                              title="Notebook Options"
                            >
                              <MoreVertical className="w-3.5 h-3.5" />
                            </button>
                          </div>

                          {activeNotebookContextMenu === nb.id && (
                            <>
                              <div
                                className="fixed inset-0 z-30 bg-transparent"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveNotebookContextMenu(null);
                                }}
                              />
                              <motion.div
                                initial={{ opacity: 0, scale: 0.95, y: 5 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                className="absolute right-2 top-8 w-44 bg-white dark:bg-slate-800 text-slate-800 dark:text-zinc-100 rounded-xl shadow-2xl p-1.5 z-40 border border-slate-200 dark:border-slate-700 select-none text-left"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {/* Lock / Unlock */}
                                <button
                                  onClick={() => {
                                    if (nb.isLocked) {
                                      setUnlockingId(nb.id);
                                      setUnlockPasscode('');
                                      setUnlockingError('');
                                    } else {
                                      setLockNotebookId(nb.id);
                                      setLockPasscode('');
                                      setLockError('');
                                    }
                                    setActiveNotebookContextMenu(null);
                                  }}
                                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-700 dark:text-slate-200 cursor-pointer text-left font-sans font-medium"
                                >
                                  {nb.isLocked ? <Unlock className="w-3.5 h-3.5 text-amber-500" /> : <Lock className="w-3.5 h-3.5" />}
                                  <span>{nb.isLocked ? 'Unlock Entry' : 'Lock Book'}</span>
                                </button>

                                {/* Share / Unshare */}
                                <button
                                  onClick={() => {
                                    // Toggle shared state directly
                                    const updated = notebooks.map((b) =>
                                      b.id === nb.id ? { ...b, isShared: !b.isShared } : b
                                    );
                                    setNotebooks(updated);
                                    localStorage.setItem(LOCAL_STORAGE_NOTEBOOKS_KEY, JSON.stringify(updated));
                                    setActiveNotebookContextMenu(null);
                                  }}
                                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-700 dark:text-slate-200 cursor-pointer text-left font-sans font-medium"
                                >
                                  <Share2 className={`w-3.5 h-3.5 ${nb.isShared ? 'text-blue-500' : ''}`} />
                                  <span>{nb.isShared ? 'Stop Sharing' : 'Share Space'}</span>
                                </button>

                                {/* Change Cover */}
                                <button
                                  onClick={() => {
                                    setChangingCoverNotebookId(nb.id);
                                    setSelectedChangeCoverPreset(nb.coverStyle);
                                    setActiveNotebookContextMenu(null);
                                  }}
                                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-700 dark:text-slate-200 cursor-pointer text-left font-sans font-medium"
                                >
                                  <Palette className="w-3.5 h-3.5" />
                                  <span>Change Cover</span>
                                </button>

                                {/* Info */}
                                <button
                                  onClick={() => {
                                    setInfoNotebook(nb);
                                    setActiveNotebookContextMenu(null);
                                  }}
                                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-700 dark:text-slate-200 cursor-pointer text-left font-sans font-medium"
                                >
                                  <Info className="w-3.5 h-3.5 text-blue-500" />
                                  <span>Book Info</span>
                                </button>

                                {/* Rename */}
                                <button
                                  onClick={() => {
                                    setRenamingNotebookId(nb.id);
                                    setRenameNotebookTitle(nb.title);
                                    setActiveNotebookContextMenu(null);
                                  }}
                                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-700 dark:text-slate-200 cursor-pointer text-left font-sans font-medium"
                                >
                                  <Edit3 className="w-3.5 h-3.5" />
                                  <span>Rename Title</span>
                                </button>

                                <div className="border-t border-slate-100 dark:border-slate-700/80 my-1" />

                                {/* Delete */}
                                <button
                                  onClick={(e) => {
                                    handleDeleteNotebook(nb.id, e);
                                    setActiveNotebookContextMenu(null);
                                  }}
                                  className="w-full flex items-center gap-2.5 px-3 py-2 text-xs hover:bg-rose-50 dark:hover:bg-rose-950/30 text-rose-600 rounded-lg cursor-pointer text-left font-sans font-semibold"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                  <span>Trash Book</span>
                                </button>
                              </motion.div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {activeTab === 'shared' && (
                <div className="flex flex-col h-full overflow-hidden justify-between">
                  <div className="p-4 border-b border-[#e9ebed] dark:border-slate-900 bg-[#f5f6f8] dark:bg-[#121417]">
                    <h2 className="text-[15px] font-bold text-slate-800 dark:text-white font-sans">Shared</h2>
                    <div className="flex items-center gap-1.5 mt-2 bg-white dark:bg-slate-900 p-1.5 px-2.5 rounded-lg border border-[#e2e4e8] dark:border-slate-800">
                      <Search className="w-3.5 h-3.5 text-slate-400" />
                      <input type="text" placeholder="Search Shared" className="bg-transparent border-none outline-none text-[11px] w-full" />
                    </div>
                    {/* Capsule category list */}
                    <div className="flex gap-1.5 mt-3 overflow-x-auto shrink-0 text-[10px] font-bold select-none cursor-pointer">
                      <span className="p-1 px-2.5 bg-slate-900 text-white rounded-full">All</span>
                      <span className="p-1 px-2.5 hover:bg-slate-200 dark:hover:bg-slate-850 rounded-full text-slate-500">Notes</span>
                      <span className="p-1 px-2.5 hover:bg-slate-200 dark:hover:bg-slate-850 rounded-full text-slate-500">Notebooks</span>
                      <span className="p-1 px-2.5 hover:bg-slate-200 dark:hover:bg-slate-850 rounded-full text-slate-500 whitespace-nowrap">Collections</span>
                    </div>
                  </div>
                  {/* Empty details graphic */}
                  <div className="flex-1 flex flex-col justify-center items-center text-center p-6 bg-slate-50/20">
                    <div className="w-12 h-12 bg-slate-200/50 dark:bg-slate-800/80 rounded-full flex items-center justify-center mb-3">
                      <Users className="w-5 h-5 text-slate-400" />
                    </div>
                    <p className="text-xs text-slate-500 font-medium leading-relaxed max-w-[150px]">
                      Uh-oh! You've got no items shared with you.
                    </p>
                  </div>
                </div>
              )}

              {activeTab === 'search' && (
                <div className="flex flex-col h-full overflow-hidden">
                  <div className="p-4 border-b border-[#e9ebed] dark:border-slate-900 bg-[#f5f6f8] dark:bg-[#121417]">
                    <div className="flex items-center gap-2 bg-white dark:bg-slate-900 p-2 px-3 rounded-lg border border-[#e2e4e8] dark:border-slate-800">
                      <Search className="w-4 h-4 text-slate-405 shrink-0" />
                      <input
                        type="text"
                        placeholder="Search by title, content, or tags"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="bg-transparent border-none outline-none text-[11px] w-full text-slate-800 dark:text-white"
                      />
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-scroll p-4 space-y-3 bg-[#f5f6f8] dark:bg-transparent auto-scrollbar min-h-0">
                    {searchQuery ? (
                      filteredCards.map((card) => {
                        const isSelected = editingCard?.id === card.id;
                        const hasTheme = card.deskThemeIdx !== undefined;
                        return (
                          <div
                            key={card.id}
                            onClick={() => setEditingCard(card)}
                            className={`p-3.5 rounded-lg border cursor-pointer relative flex flex-col justify-between h-[105px] transition-all select-none ${isSelected
                              ? 'ring-2 ring-indigo-500 border-transparent shadow-md scale-[1.02]'
                              : hasTheme
                                ? 'hover:translate-y-[-1px] hover:shadow-xs'
                                : 'hover:translate-y-[-1px] hover:shadow-xs border-amber-300/40 bg-amber-50/70 dark:bg-amber-950/20'
                              } ${hasTheme ? '' : card.colorClass}`}
                            style={{
                              backgroundColor: hasTheme ? DESK_THEMES[card.deskThemeIdx!].bgValue : undefined,
                              borderColor: isSelected
                                ? 'transparent'
                                : (hasTheme ? DESK_THEMES[card.deskThemeIdx!].accentBorder : undefined),
                              color: hasTheme ? DESK_THEMES[card.deskThemeIdx!].textValue : undefined,
                            }}
                          >
                            <div className="flex-1 min-w-0">
                              <h4 className="font-bold text-xs text-slate-900 dark:text-slate-100 truncate line-clamp-1 leading-snug">
                                {card.title || 'Untitled Note'}
                              </h4>
                              <p className="text-[11px] text-slate-600 dark:text-slate-400 font-medium mt-1 leading-normal line-clamp-2 truncate overflow-hidden">
                                {stripHtml(card.textContent) || (card.type === 'checklist' ? 'Checklist values configured' : 'Note details representation')}
                              </p>
                            </div>
                            <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-black/[0.04] text-[9.5px] font-mono text-slate-400 select-none">
                              <span>Today</span>
                              {card.isFavorite && <Heart className="w-2.5 h-2.5 text-rose-500 fill-rose-500" />}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-12 text-xs text-slate-400 font-medium italic">
                        Enter a search term
                      </div>
                    )}
                    {searchQuery && filteredCards.length === 0 && (
                      <div className="text-center py-12 text-xs text-slate-400 font-medium italic">
                        No notes found
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'reminders' && (
                <div className="flex flex-col h-full overflow-hidden">
                  <div className="p-4 flex items-center justify-between border-b border-[#e9ebed] dark:border-slate-900 bg-[#f5f6f8] dark:bg-[#121417]">
                    <h2 className="text-[15px] font-bold text-slate-800 dark:text-white font-sans">Reminders</h2>
                    <button className="p-1 text-slate-400 hover:text-slate-900"><MoreHorizontal className="w-4 h-4" /></button>
                  </div>
                  {/* Toggle Pills sub-bar */}
                  <div className="p-4 py-2 bg-slate-50/40 flex gap-2">
                    <button
                      onClick={() => setRemindersSubTab('overdue')}
                      className={`text-xs font-bold py-1 px-3.5 rounded-full ${remindersSubTab === 'overdue'
                        ? 'bg-slate-900 text-white shadow-xs'
                        : 'text-slate-500 hover:bg-slate-200/50'
                        }`}
                    >
                      Overdue
                    </button>
                    <button
                      onClick={() => setRemindersSubTab('upcoming')}
                      className={`text-xs font-bold py-1 px-3.5 rounded-full ${remindersSubTab === 'upcoming'
                        ? 'bg-slate-900 text-white shadow-xs'
                        : 'text-slate-500 hover:bg-slate-200/50'
                        }`}
                    >
                      Upcoming
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#f5f6f8] dark:bg-transparent">
                    {filteredCards.map((card) => {
                      const isSelected = editingCard?.id === card.id;
                      const hasTheme = card.deskThemeIdx !== undefined;
                      return (
                        <div
                          key={card.id}
                          onClick={() => setEditingCard(card)}
                          className={`p-3.5 rounded-lg border cursor-pointer relative flex flex-col justify-between h-[105px] transition-all select-none ${isSelected
                            ? 'ring-2 ring-indigo-500 border-transparent shadow-md scale-[1.02]'
                            : hasTheme
                              ? 'hover:translate-y-[-1px] hover:shadow-xs'
                              : 'hover:translate-y-[-1px] hover:shadow-xs border-amber-300/40 bg-amber-50/70 dark:bg-amber-950/20'
                            } ${hasTheme ? '' : card.colorClass}`}
                          style={{
                            backgroundColor: hasTheme ? DESK_THEMES[card.deskThemeIdx!].bgValue : undefined,
                            borderColor: isSelected
                              ? 'transparent'
                              : (hasTheme ? DESK_THEMES[card.deskThemeIdx!].accentBorder : undefined),
                            color: hasTheme ? DESK_THEMES[card.deskThemeIdx!].textValue : undefined,
                          }}
                        >
                          <div className="flex-1 min-w-0">
                            <h4 className="font-bold text-xs text-slate-900 dark:text-slate-100 truncate line-clamp-1 leading-snug">
                              {card.title || 'Untitled Note'}
                            </h4>
                            <p className="text-[11px] text-slate-600 dark:text-slate-400 font-medium mt-1 leading-normal line-clamp-2 truncate overflow-hidden">
                              {stripHtml(card.textContent) || (card.type === 'checklist' ? 'Checklist values configured' : 'Note details representation')}
                            </p>
                          </div>
                          <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-black/[0.04] text-[9.5px] font-mono text-slate-400 select-none">
                            <span>Today</span>
                            {card.isFavorite && <Heart className="w-2.5 h-2.5 text-rose-500 fill-rose-500" />}
                          </div>
                        </div>
                      );
                    })}
                    {filteredCards.length === 0 && (
                      <div className="text-center py-12 text-xs text-slate-400 font-medium italic">
                        No reminders available
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'tags' && (
                <div className="flex flex-col h-full overflow-hidden">
                  <div className="p-4 border-b border-[#e9ebed] dark:border-slate-900 bg-[#f5f6f8] dark:bg-[#121417]">
                    <h2 className="text-[15px] font-bold text-slate-800 dark:text-white font-sans">Tags</h2>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {allUniqueTags.map(tag => (
                        <button
                          key={tag}
                          onClick={() => setSelectedTag(tag === selectedTag ? null : tag)}
                          className={`text-[10px] font-bold py-1 px-2.5 rounded-full ${selectedTag === tag ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#f5f6f8] dark:bg-transparent">
                    {filteredCards.map((card) => {
                      const isSelected = editingCard?.id === card.id;
                      const hasTheme = card.deskThemeIdx !== undefined;
                      return (
                        <div
                          key={card.id}
                          onClick={() => setEditingCard(card)}
                          className={`p-3.5 rounded-lg border cursor-pointer relative flex flex-col justify-between h-[105px] transition-all select-none ${isSelected
                            ? 'ring-2 ring-indigo-500 border-transparent shadow-md scale-[1.02]'
                            : hasTheme
                              ? 'hover:translate-y-[-1px] hover:shadow-xs'
                              : 'hover:translate-y-[-1px] hover:shadow-xs border-amber-300/40 bg-amber-50/70 dark:bg-amber-950/20'
                            } ${hasTheme ? '' : card.colorClass}`}
                          style={{
                            backgroundColor: hasTheme ? DESK_THEMES[card.deskThemeIdx!].bgValue : undefined,
                            borderColor: isSelected
                              ? 'transparent'
                              : (hasTheme ? DESK_THEMES[card.deskThemeIdx!].accentBorder : undefined),
                            color: hasTheme ? DESK_THEMES[card.deskThemeIdx!].textValue : undefined,
                          }}
                        >
                          <div className="flex-1 min-w-0">
                            <h4 className="font-bold text-xs text-slate-900 dark:text-slate-100 truncate line-clamp-1 leading-snug">
                              {card.title || 'Untitled Note'}
                            </h4>
                            <p className="text-[11px] text-slate-600 dark:text-slate-400 font-medium mt-1 leading-normal line-clamp-2 truncate overflow-hidden">
                              {stripHtml(card.textContent) || (card.type === 'checklist' ? 'Checklist values configured' : 'Note details representation')}
                            </p>
                          </div>
                          <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-black/[0.04] text-[9.5px] font-mono text-slate-400 select-none">
                            <span>Today</span>
                            {card.isFavorite && <Heart className="w-2.5 h-2.5 text-rose-500 fill-rose-500" />}
                          </div>
                        </div>
                      );
                    })}
                    {filteredCards.length === 0 && (
                      <div className="text-center py-12 text-xs text-slate-400 font-medium italic">
                        No notes with selected tag
                      </div>
                    )}
                  </div>
                </div>
              )}

            </motion.aside>
          )}
        </AnimatePresence>

        {/* COLUMN 3: SELECTED NOTEBOOK PAGES SIDEBAR (ACTIVE ONLY WHEN NOTEBOOKS TAB SELECTS A NOTEBOOK) */}
        {activeTab === 'notebooks' && selectedNotebookId && (
          <motion.aside
            initial={{ width: 0, opacity: 0 }}
            animate={{
              width: isPagesSidebarCollapsed ? 0 : 260,
              opacity: isPagesSidebarCollapsed ? 0 : 1,
            }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className={`shrink-0 bg-[#f8f9fb] dark:bg-[#15171b] flex flex-col h-full overflow-hidden select-none z-10 ${isPagesSidebarCollapsed ? 'border-r-0' : 'border-r border-[#eceef1] dark:border-slate-900'}`}
          >
            <div className="p-4 py-3 border-b border-[#e9ebed] dark:border-slate-900 selection-header flex flex-col justify-between h-[65px] bg-[#f8f9fb] dark:bg-[#15171b]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="min-w-0">
                    <h3 className="text-[14px] font-bold text-slate-800 dark:text-white truncate max-w-[130px] font-sans leading-tight">
                      {currentNotebook ? currentNotebook.title : 'My Notecards'}
                    </h3>
                    <span className="text-[10px] text-slate-400 block font-medium mt-0.5 select-none leading-none">
                      {filteredCards.length} note{filteredCards.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <button
                    onClick={() => setIsPagesSidebarCollapsed(true)}
                    className="p-1 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-md text-slate-500 hover:text-slate-850 dark:hover:text-white cursor-pointer shrink-0"
                    title="Minimize Sidebar"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex items-center gap-1.5 text-slate-400 shrink-0">
                  <button
                    onClick={() => handleCreateCard('text')}
                    className="p-1 hover:bg-slate-200 dark:hover:bg-slate-800 text-slate-700 dark:text-white rounded-md shrink-0 cursor-pointer"
                    title="Add Card"
                  >
                    <Plus className="w-3.5 h-3.5 stroke-[2.5]" />
                  </button>
                  <button onClick={() => showToast("Book marked! Enjoy organizing notes.", 'success')} className="p-1 hover:text-slate-800 dark:hover:text-white cursor-pointer"><Heart className="w-3.5 h-3.5" /></button>
                  <button className="p-1 hover:text-slate-800 dark:hover:text-white cursor-pointer"><Search className="w-3.5 h-3.5" /></button>
                  <button className="p-1 hover:text-slate-800 dark:hover:text-white cursor-pointer"><MoreHorizontal className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>

            {/* scrolling cards content in the notebook pages */}
            <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-[#f8f9fb] dark:bg-transparent">
              {filteredCards.map((card) => {
                const isSelected = activeCard?.id === card.id;
                const hasTheme = card.deskThemeIdx !== undefined;
                return (
                  <div
                    key={card.id}
                    onClick={() => setEditingCard(card)}
                    className={`p-3.5 rounded-lg border cursor-pointer relative flex flex-col justify-between h-[105px] transition-all select-none ${isSelected
                      ? 'ring-2 ring-indigo-500 border-transparent shadow-xs'
                      : hasTheme
                        ? 'hover:translate-y-[-1px] hover:shadow-xs'
                        : 'hover:translate-y-[-1px] hover:shadow-xs border-amber-250 bg-amber-50/70 dark:bg-amber-950/20'
                      } ${hasTheme ? '' : card.colorClass}`}
                    style={{
                      backgroundColor: hasTheme ? DESK_THEMES[card.deskThemeIdx!].bgValue : undefined,
                      borderColor: isSelected
                        ? 'transparent'
                        : (hasTheme ? DESK_THEMES[card.deskThemeIdx!].accentBorder : undefined),
                      color: hasTheme ? DESK_THEMES[card.deskThemeIdx!].textValue : undefined,
                    }}
                  >
                    <div>
                      <h4 className="font-bold text-xs text-slate-900 dark:text-slate-100 line-clamp-1 truncate leading-snug">
                        {card.title || 'Untitled Note'}
                      </h4>
                      <p className="text-[11px] text-slate-600 dark:text-slate-400 mt-1 lines-clamp-3 leading-normal truncate block overflow-hidden max-h-[3.3em]">
                        {stripHtml(card.textContent) || 'Sticky note details...'}
                      </p>
                    </div>

                    <div className="text-[9.5px] font-mono text-slate-400 flex items-center justify-between border-t border-black/[0.04] pt-1 mt-2 shrink-0 select-none">
                      <span>Today</span>
                      {card.isFavorite && <Heart className="w-2.5 h-2.5 text-rose-500 fill-rose-500" />}
                    </div>
                  </div>
                );
              })}

              {filteredCards.length === 0 && (
                <div className="text-center py-12 text-xs text-slate-400 font-medium font-sans">
                  No note cards in folder
                </div>
              )}
            </div>
          </motion.aside>
        )}

        {/* COLUMN 4 / WORKSPACE AREA: ACTIVE NOTE EDITOR OR DUSTY CLOSET LANDING GRID / CANVAS */}
        <main className="flex-1 bg-white dark:bg-slate-900 flex flex-col overflow-hidden h-full relative">

          {!(editingCard && editorLayoutMode === 'full') && (
            /* CONDITIONAL MAIN CONTAINER RENDER BASED ON ACTIVE TAB */
            <div className="flex-1 flex flex-col overflow-hidden w-full h-full bg-[#fcfdfe] dark:bg-[#14161b]">
              <div style={{ display: (activeTab === 'docs' || activeTab === 'canvas') ? 'block' : 'none', width: '100%', height: '100%' }}>
                <DesignStudioEditor mode={activeTab === 'canvas' ? 'edgeless' : 'page'} />
              </div>
              {activeTab !== 'canvas' && activeTab !== 'docs' && (
                activeTab === 'noteboards' ? (
                  /* FULLY INTERACTIVE DEDICATED NOTEBOARD KANBAN WORKSPACE */
                  <NoteboardWorkspace
                    cards={cards}
                    setCards={setCards}
                    notebooks={notebooks}
                    setEditingCard={setEditingCard}
                    editingCard={editingCard}
                    isDarkMode={isDarkMode}
                    onClose={() => setActiveTab('all_notes')}
                  />
                ) : activeTab === 'notebooks' && selectedNotebookId === null ? (
                  /* NOTEBOOK SHELF VIEW - ENTIRE NOTEBOOKS COLLECTION RENDERED AS PORTRAIT COVERS IN AN ELEGANT LIBRARY Grid */
                  <div className="flex-1 flex flex-col h-full bg-white dark:bg-[#111317] overflow-hidden select-none animate-in fade-in duration-300">
                    {/* Library Command bar strip matching original design precision */}
                    <div className="p-4 px-6 md:px-8 border-b border-[#e9ebed] dark:border-slate-900 bg-[#fbfcfd] dark:bg-[#111317] flex flex-wrap items-center justify-between gap-4 shrink-0">
                      <div>
                        <h2 className="text-lg font-sans font-extrabold tracking-tight text-slate-800 dark:text-white flex items-center gap-2 leading-none">
                          <FolderOpen className="text-amber-500 w-5 h-5 shrink-0" />
                          My Notecards Library
                        </h2>
                        <span className="text-[11px] text-slate-400 font-medium block mt-1 leading-none">
                          {activeNotebooksList.length} functional journal books
                        </span>
                      </div>

                      <div className="flex items-center gap-3">
                        {/* Search box filters of covers */}
                        <div className="relative w-44 md:w-52 flex items-center bg-slate-100 dark:bg-slate-900 px-3 py-1.5 rounded-full border border-transparent focus-within:border-slate-200 dark:focus-within:border-slate-800 transition-all">
                          <Search className="w-3.5 h-3.5 text-slate-400 mr-2 shrink-0" />
                          <input
                            type="text"
                            placeholder="Search folders..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="bg-transparent border-none outline-none text-[11px] w-full text-slate-700 dark:text-white placeholder-slate-400/80"
                          />
                        </div>

                        {/* Sort option selection drop */}
                        <select
                          value={sortOption}
                          onChange={(e) => setSortOption(e.target.value as SortOption)}
                          className="text-xs bg-slate-100 dark:bg-slate-900 border-none p-1.5 px-3 rounded-full text-slate-600 dark:text-slate-350 font-medium cursor-pointer focus:outline-none focus:ring-0"
                        >
                          <option value="updated-desc">Recently Updated</option>
                          <option value="created-desc">Date Created</option>
                          <option value="title-asc">Title A-Z</option>
                          <option value="title-desc">Title Z-A</option>
                        </select>

                        {/* Add Notebook primary cover trigger button */}
                        <button
                          onClick={() => setIsCreatingNotebook(true)}
                          className="flex items-center gap-1.5 px-4 py-1.5 bg-[#1e222b] hover:bg-black text-white text-xs font-bold rounded-full transition-all shadow-sm active:scale-95 cursor-pointer"
                        >
                          <Plus className="w-3.5 h-3.5 stroke-[2.5]" />
                          <span>Add Book</span>
                        </button>
                      </div>
                    </div>

                    {/* Covers bookshelf canvas area */}
                    <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-[#fbfcfd] dark:bg-slate-950 auto-scrollbar min-h-0">
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6 md:gap-8 justify-items-center mb-12">

                        {/* Standard skeuomorphic 'Create Notebook' blank patterned cover */}
                        <div
                          onClick={() => setIsCreatingNotebook(true)}
                          className="w-44 h-60 rounded-r-xl border-[2px] border-dashed border-slate-300 dark:border-slate-800 flex flex-col justify-center items-center text-center p-4 hover:border-amber-500 hover:text-amber-500 hover:bg-slate-50/50 dark:hover:bg-slate-900/10 transition-all duration-300 cursor-pointer select-none shadow-xs hover:-translate-y-1.5 group"
                        >
                          <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-900 group-hover:bg-amber-100/50 flex items-center justify-center mb-3 text-slate-400 group-hover:text-amber-600 transition-colors">
                            <Plus className="w-5 h-5 stroke-[2.5]" />
                          </div>
                          <span className="text-xs font-bold text-slate-500 group-hover:text-amber-600">Customize Cover</span>
                          <p className="text-[10px] text-slate-400 mt-1 max-w-[110px] leading-snug">Build custom notebook journal spine</p>
                        </div>

                        {/* Map active cover objects with counts and names underlying */}
                        {activeNotebooksList.map((nb) => {
                          const count = notebookCardsCounter[nb.id] || 0;
                          return (
                            <div key={nb.id} className="flex flex-col items-center group/cover select-none relative w-44">
                              <NotebookCover
                                title={nb.title}
                                coverStyle={nb.coverStyle}
                                cardCount={count}
                                onClick={() => handleSelectNotebook(nb.id)}
                              />

                              {/* Aligned details panel underneath */}
                              <div className="w-full mt-3 flex items-start justify-between px-1 bg-transparent">
                                <div className="min-w-0 flex-1 text-left">
                                  <span className="text-[13px] font-bold text-slate-700 dark:text-slate-350 truncate block group-hover/cover:text-amber-500 leading-snug">
                                    {nb.title}
                                  </span>
                                  <span className="text-[10.5px] text-slate-400 font-semibold mt-0.5 block leading-none">
                                    {count} note{count === 1 ? '' : 's'}
                                  </span>
                                </div>

                                <div className="opacity-0 group-hover/cover:opacity-100 flex items-center gap-1 transition-opacity z-15 shrink-0 ml-1">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setRenamingNotebookId(nb.id);
                                      setRenameNotebookTitle(nb.title);
                                    }}
                                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-700 dark:hover:text-white rounded-md shrink-0 cursor-pointer"
                                    title="Rename"
                                  >
                                    <Edit3 className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={(e) => handleDeleteNotebook(nb.id, e)}
                                    className="p-1 hover:bg-rose-500/10 text-slate-400 hover:text-rose-600 rounded-md shrink-0 cursor-pointer"
                                    title="Archive / Delete"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : activeTab === 'notebooks' && selectedNotebookId ? (
                  /* NOTEBOOK PAGE EDITOR — full width, no overlap with sidebars */
                  <NotebookPageEditor
                    notebookId={selectedNotebookId}
                    notebookTitle={currentNotebook?.title || 'Untitled Notebook'}
                    onBack={() => setSelectedNotebookId(null)}
                    isPagesSidebarCollapsed={isPagesSidebarCollapsed}
                    setIsPagesSidebarCollapsed={setIsPagesSidebarCollapsed}
                    activeCardId={activeCard?.id}
                    activeCardTitle={activeCard?.title}
                    onTitleChange={(newTitle) => {
                      if (activeCard) {
                        handleUpdateCardTitle(activeCard.id, newTitle);
                      }
                    }}
                  />
                ) : (
                  /* NOTEBOOK DUSTY BOARD DESK / STICKY NOTES CANVAS WORKSPACE GRID (SELECTED NOTEBOOK OR ALL NOTES DASHBOARD) */
                  <div className="flex-1 flex flex-col h-full bg-white dark:bg-[#111317] overflow-hidden animate-in fade-in duration-200">

                    {/* Top Bar Desk Header matches layout precision */}
                    <div className="p-4 px-6 md:px-8 border-b border-[#e9ebed] dark:border-slate-900 bg-[#fbfcfd] dark:bg-[#111317] flex flex-wrap items-center justify-between gap-4 shrink-0 select-none">
                      <div className="flex items-center gap-3">
                        {activeTab === 'notebooks' && selectedNotebookId && (
                          <button
                            onClick={() => setSelectedNotebookId(null)}
                            className="p-1 px-2.5 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-205 dark:border-slate-800 rounded-lg text-xs font-bold text-slate-500 hover:text-slate-900 dark:hover:text-white flex items-center gap-1 cursor-pointer transition-colors"
                            title="Back to notebook library"
                          >
                            <ArrowLeft className="w-3.5 h-3.5" />
                            <span>Library</span>
                          </button>
                        )}
                        <div>
                          <div className="flex items-center gap-1.5">
                            <h2 className="text-lg font-sans font-extrabold tracking-tight text-slate-800 dark:text-white leading-tight">
                              {activeTab === 'all_notes' ? 'All Notes' : (currentNotebook ? currentNotebook.title : 'My Notecards')}
                            </h2>
                            {activeTab === 'notebooks' && selectedNotebookId && (
                              <button
                                onClick={() => {
                                  setRenamingNotebookId(selectedNotebookId);
                                  setRenameNotebookTitle(currentNotebook?.title || '');
                                }}
                                className="p-1 text-slate-400 hover:text-slate-800 dark:hover:text-white cursor-pointer"
                              >
                                <Edit3 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                          <span className="text-[11px] text-slate-400 font-semibold block mt-0.5 leading-none">
                            {filteredCards.length} matching sticky card{filteredCards.length === 1 ? '' : 's'}
                          </span>
                        </div>
                      </div>

                      {/* Controls (Layout Grid/List switcher & Sorting) */}
                      <div className="flex items-center gap-3">
                        {/* Layout toggle pill */}
                        <div className="bg-slate-100 dark:bg-slate-905 p-0.5 rounded-lg flex items-center border border-slate-200 dark:border-slate-800 shrink-0">
                          <button
                            onClick={() => setViewLayout('grid')}
                            className={`p-1.5 rounded-md cursor-pointer transition-all ${viewLayout === 'grid'
                              ? 'bg-white dark:bg-slate-800 text-slate-950 dark:text-white shadow-xs'
                              : 'text-slate-400 hover:text-slate-700 dark:hover:text-slate-350'
                              }`}
                            title="Show as Grid"
                          >
                            <Grid className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setViewLayout('list')}
                            className={`p-1.5 rounded-md cursor-pointer transition-all ${viewLayout === 'list'
                              ? 'bg-white dark:bg-slate-800 text-slate-950 dark:text-white shadow-xs'
                              : 'text-slate-400 hover:text-slate-700 dark:hover:text-slate-350'
                              }`}
                            title="Show as List"
                          >
                            <List className="w-3.5 h-3.5" />
                          </button>
                        </div>

                        {/* Sort option selection drop */}
                        <select
                          value={sortOption}
                          onChange={(e) => setSortOption(e.target.value as SortOption)}
                          className="text-xs bg-slate-100 dark:bg-slate-900 border-none p-1.5 px-3 rounded-full text-slate-600 dark:text-slate-350 font-medium cursor-pointer focus:outline-none focus:ring-0"
                        >
                          <option value="updated-desc">Date Edited (Newest)</option>
                          <option value="updated-asc">Date Edited (Oldest)</option>
                          <option value="created-desc">Date Created</option>
                          <option value="title-asc">Title (A-Z)</option>
                          <option value="title-desc">Title (Z-A)</option>
                        </select>

                        {/* Header quick inline filter */}
                        <div className="relative w-40 md:w-48 flex items-center bg-slate-100 dark:bg-slate-900 px-3 py-1.5 rounded-full border border-transparent focus-within:border-slate-200 dark:focus-within:border-slate-800 transition-all shrink-0">
                          <Search className="w-3.5 h-3.5 text-slate-400 mr-1.5 shrink-0" />
                          <input
                            type="text"
                            placeholder="Search folders..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="bg-transparent border-none outline-none text-[10.5px] w-full text-slate-705 dark:text-white placeholder-slate-450/80"
                          />
                        </div>
                      </div>
                    </div>

                    {/* QUICK CREATOR BAR (SHORT STRIP TRAY FOR LAUNCHING REAL CARD BUILDERS) */}
                    <div className="p-3.5 bg-slate-50/50 dark:bg-[#111317] border-b border-[#e9ebed] dark:border-slate-900 flex justify-center items-center shrink-0">
                      <div className="bg-white dark:bg-slate-950 rounded-full border border-[#dee0e3] dark:border-slate-800 p-1 px-3 flex items-center gap-2 md:gap-3 shadow-xs shrink-0 select-none">
                        <span className="text-[10px] uppercase font-mono tracking-widest text-slate-400 font-bold mr-1 block">Quick Add :</span>

                        {/* Text Note creator */}
                        <button
                          onClick={() => handleCreateCard('text')}
                          className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-850 border border-slate-100 dark:border-slate-900 text-xs font-bold text-slate-705 dark:text-slate-300 transition-all cursor-pointer"
                        >
                          <Edit3 className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                          <span className="text-[11px] font-sans">Write</span>
                        </button>

                        {/* Check List Creator */}
                        <button
                          onClick={() => handleCreateCard('checklist')}
                          className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-850 border border-slate-100 dark:border-slate-900 text-xs font-bold text-slate-705 dark:text-slate-300 transition-all cursor-pointer"
                        >
                          <CheckSquare className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                          <span className="text-[11px] font-sans">To Do</span>
                        </button>

                        {/* Sketchpad Draw Creator */}
                        <button
                          onClick={() => handleCreateCard('sketch')}
                          className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-850 border border-slate-100 dark:border-slate-900 text-xs font-bold text-slate-705 dark:text-slate-300 transition-all cursor-pointer"
                        >
                          <Paintbrush className="w-3.5 h-3.5 text-purple-500 shrink-0" />
                          <span className="text-[11px] font-sans">Draw</span>
                        </button>

                        {/* Camera Photo capture creator */}
                        <button
                          onClick={() => handleCreateCard('photo')}
                          className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-850 border border-slate-100 dark:border-slate-900 text-xs font-bold text-slate-705 dark:text-slate-300 transition-all cursor-pointer"
                        >
                          <Camera className="w-3.5 h-3.5 text-sky-500 shrink-0" />
                          <span className="text-[11px] font-sans">Capture</span>
                        </button>

                        {/* Audio Voice Recorder creator */}
                        <button
                          onClick={() => handleCreateCard('audio')}
                          className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-850 border border-slate-100 dark:border-slate-900 text-xs font-bold text-slate-705 dark:text-slate-300 transition-all cursor-pointer"
                        >
                          <Volume2 className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                          <span className="text-[11px] font-sans">Voice</span>
                        </button>
                      </div>
                    </div>

                    {/* NOTE CARDS DESK BOARD GRID */}
                    <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-[#fbfcfd] dark:bg-slate-950 auto-scrollbar min-h-0">
                      {filteredCards.length === 0 ? (
                        /* Zero state overlay container */
                        <div className="h-full flex flex-col items-center justify-center text-center max-w-md mx-auto py-12 select-none">
                          {/* Stacked sheets illustration */}
                          <div className="relative mb-6 flex justify-center scale-95 select-none animate-in fade-in zoom-in-95 duration-400">
                            <div className="w-16 h-22 bg-[#4285f4] rounded-r-lg border-l-[4px] border-[#3372da] shadow-md relative flex flex-col justify-between p-2 flex-none text-white/50 font-black leading-none text-xs">
                              <div className="w-full h-1 bg-white/20 rounded-sm" />
                              <span>NB</span>
                            </div>
                            <div className="w-16 h-22 bg-[#fbbc05] rounded-r-lg border-l-[4px] border-[#e2a803] shadow-md absolute translate-x-3 translate-y-3 flex-none text-white/80 font-black leading-none text-xs p-2 flex flex-col justify-between">
                              <div className="w-full h-1 bg-white/30 rounded-sm" />
                              <span>ZO</span>
                            </div>
                          </div>

                          <h2 className="text-[19px] font-extrabold text-slate-800 dark:text-white mb-1.5 px-4 tracking-tight leading-snug">
                            Start jotting down your ideas
                          </h2>
                          <p className="text-xs text-slate-400 max-w-xs mb-8 leading-relaxed">
                            This folder has no sticky note cards. Use the Quick Add strip above or write down your first task today!
                          </p>

                          <div className="grid grid-cols-2 gap-3 w-full max-w-[340px]">
                            <button
                              onClick={() => handleCreateCard('text')}
                              className="flex items-center justify-center p-3 border border-slate-205 dark:border-slate-800 bg-[#f4f6f8] dark:bg-slate-900 rounded-xl text-xs font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer shadow-xxs transition-transform active:scale-95"
                            >
                              <Edit3 className="w-3.5 h-3.5 text-slate-450 mr-1.5 shrink-0" />
                              <span>Write Text</span>
                            </button>
                            <button
                              onClick={() => handleCreateCard('checklist')}
                              className="flex items-center justify-center p-3 border border-slate-205 dark:border-slate-800 bg-[#f4f6f8] dark:bg-slate-900 rounded-xl text-xs font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer shadow-xxs transition-transform active:scale-95"
                            >
                              <CheckSquare className="w-3.5 h-3.5 text-slate-450 mr-1.5 shrink-0" />
                              <span>To Do List</span>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="w-full">
                          {viewLayout === 'grid' ? (
                            /* COLUMNAR DENSE RESPONSIVE SWATCH GRID */
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                              {filteredCards.map((card) => (
                                <NoteCardItem
                                  key={card.id}
                                  card={card}
                                  onClick={() => setEditingCard(card)}
                                  onToggleFavorite={toggleFavoriteValue}
                                  onDelete={deleteCardToBin}
                                  onDuplicate={duplicateCard}
                                />
                              ))}
                            </div>
                          ) : (
                            /* VERTICAL FLAT ROW LIST STYLE */
                            <div className="max-w-4xl mx-auto space-y-4">
                              {filteredCards.map((card) => {
                                const hasTheme = card.deskThemeIdx !== undefined;
                                return (
                                  <div
                                    key={card.id}
                                    onClick={() => setEditingCard(card)}
                                    className={`p-4 py-5 rounded-xl border cursor-pointer relative flex items-center justify-between transition-all select-none hover:shadow-md hover:scale-[1.005] ${hasTheme ? '' : card.colorClass}`}
                                    style={{
                                      backgroundColor: hasTheme ? DESK_THEMES[card.deskThemeIdx!].bgValue : undefined,
                                      borderColor: hasTheme ? DESK_THEMES[card.deskThemeIdx!].accentBorder : undefined,
                                      color: hasTheme ? DESK_THEMES[card.deskThemeIdx!].textValue : undefined,
                                    }}
                                  >
                                    <div className="min-w-0 flex-1 flex items-center gap-4 text-left">
                                      <div className="w-2.5 h-11 rounded-full bg-slate-900/10 shrink-0" />
                                      <div className="min-w-0 flex-1">
                                        <h4 className="font-bold text-sm text-slate-900 dark:text-white truncate">
                                          {card.title || 'Untitled Note'}
                                        </h4>
                                        <p className="text-xs text-slate-655 dark:text-slate-350 truncate mt-1">
                                          {stripHtml(card.textContent) || (card.type === 'checklist' ? 'Checklist values configured' : 'Note details representation')}
                                        </p>
                                      </div>
                                    </div>

                                    <div className="flex items-center gap-3 shrink-0 ml-4">
                                      <span className="text-[10px] font-mono text-slate-400">
                                        {new Date(card.updatedAt).toLocaleDateString()}
                                      </span>
                                      {card.isFavorite && <Heart className="w-4 h-4 text-rose-500 fill-rose-500" />}

                                      {/* Trash trigger */}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          deleteCardToBin(card.id, e);
                                        }}
                                        className="p-1 px-2 rounded-lg hover:bg-black/10 text-slate-400 hover:text-rose-600 cursor-pointer"
                                        title="Trash note"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}

          <AnimatePresence>
            {editingCard && activeTab !== 'notebooks' && (
              <motion.div
                layout
                initial={{ opacity: 0, scale: 0.85, x: 50 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                exit={{ opacity: 0, scale: 0.85, x: 50 }}
                transition={{ type: 'spring', stiffness: 350, damping: 28 }}
                className={editorLayoutMode === 'popup'
                  ? "absolute top-4 right-4 bottom-4 w-[520px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl z-50 overflow-hidden flex flex-col pointer-events-auto origin-right"
                  : "absolute inset-0 bg-white dark:bg-slate-900 z-50 overflow-hidden flex flex-col pointer-events-auto"
                }
              >
                <NoteEditorInline
                  key={editingCard.id}
                  card={editingCard as NoteCard}
                  onSave={saveSelectedCard}
                  onCancel={() => setEditingCard(null)}
                  allTags={allUniqueTags}
                  onDelete={deleteCardToBin}
                  onDuplicate={duplicateCard}
                  onToggleFavorite={toggleFavoriteValue}
                  setIsNotebooksSidebarCollapsed={setIsNotebooksSidebarCollapsed}
                  setIsPagesSidebarCollapsed={setIsPagesSidebarCollapsed}
                  notebooks={notebooks}
                  isFullscreen={editorLayoutMode === 'full'}
                  onToggleFullscreen={toggleEditorFullscreen}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </main>

      </div>

      {/* 3. MODAL: RENAME NOTEBOOK DIALOG SCREEN */}
      {renamingNotebookId && (
        <div className="fixed inset-0 bg-slate-950/45 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-905 rounded-2xl max-w-sm w-full p-6 shadow-2xl border border-slate-100 dark:border-slate-800">
            <h3 className="font-bold text-lg mb-3">Rename Notebook</h3>
            <input
              type="text"
              value={renameNotebookTitle}
              onChange={(e) => setRenameNotebookTitle(e.target.value)}
              className="w-full px-4 py-2 bg-slate-50 border rounded-xl focus:outline-none dark:bg-slate-950"
              required
              autoFocus
            />
            <div className="flex gap-2 justify-end mt-4 text-xs font-bold font-sans uppercase">
              <button onClick={() => setRenamingNotebookId(null)} className="px-4 py-2 border rounded-lg text-slate-600 cursor-pointer">Cancel</button>
              <button
                onClick={() => {
                  handleRenameNotebook(renamingNotebookId, renameNotebookTitle.trim());
                  setRenamingNotebookId(null);
                }}
                className="px-4 py-2 bg-zinc-805 bg-slate-900 text-white rounded-lg cursor-pointer"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 4. MODAL: CREATE NEW NOTEBOOK COVERS & DESIGNS */}
      {isCreatingNotebook && (
        <div className="fixed inset-0 bg-slate-950/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <form
            onSubmit={handleCreateNotebook}
            className="bg-white dark:bg-slate-900 rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-slate-100 dark:border-slate-800 animate-in zoom-in-95 duration-150 flex flex-col gap-5"
          >
            <div className="flex items-center justify-between border-b pb-3 border-slate-100 dark:border-slate-800">
              <h3 className="font-sans font-bold text-lg text-slate-900 dark:text-white">Create Custom Bookshelf NoteBook</h3>
              <button
                type="button"
                onClick={() => setIsCreatingNotebook(false)}
                className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full text-slate-400"
              >
                <X className="w-5 h-5 focus:outline-none" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">              {/* Cover Art Visualizer */}
              <div className="flex flex-col items-center">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 select-none">Live Cover preview</span>
                <NotebookCover
                  title={newNotebookTitle || "New Journal Book Cover"}
                  coverStyle={{
                    type: selectedPreset.type as 'gradient' | 'pattern' | 'solid' | 'image',
                    primaryColor: selectedPreset.primaryColor,
                    secondaryColor: selectedPreset.secondaryColor,
                    patternName: selectedPreset.patternName,
                    imageTheme: (selectedPreset as any).imageTheme
                  }}
                  className="scale-[0.80] origin-center -my-6"
                />
              </div>

              {/* Cover presets customization config forms */}
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-1">Notebook Title</label>
                  <input
                    type="text"
                    value={newNotebookTitle}
                    onChange={(e) => setNewNotebookTitle(e.target.value)}
                    placeholder="E.g., Workspace Tasks, Travel logs..."
                    className="w-full px-4 py-2 border border-slate-205 bg-slate-50 text-slate-800 dark:bg-slate-950 dark:border-slate-800 dark:text-white rounded-xl text-xs font-medium focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    maxLength={32}
                    required
                    autoFocus
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-1.5">Select Cover Artwork Theme</label>
                  <div className="grid grid-cols-4 gap-2">
                    {COVERS_PRESETS.map((pst, idx) => {
                      const buttonBg = pst.type === 'gradient'
                        ? { backgroundImage: `linear-gradient(135deg, ${pst.primaryColor}, ${pst.secondaryColor})` }
                        : { backgroundColor: pst.primaryColor };

                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setSelectedPreset(pst)}
                          className={`w-[48px] h-[64px] rounded-r-md border border-black/10 shadow-xs relative overflow-hidden focus:outline-none transition-all ${selectedPreset.name === pst.name ? 'ring-2 ring-indigo-500 ring-offset-2 scale-105' : 'hover:scale-[1.03]'
                            }`}
                          style={buttonBg}
                          title={pst.name}
                        >
                          <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-black/15" />
                          <div className="absolute left-[2px] top-0 bottom-0 w-[0.5px] bg-white/20" />
                          {selectedPreset.name === pst.name && (
                            <div className="absolute inset-0 bg-black/10 flex items-center justify-center">
                              <Check className="w-4 h-4 text-white font-bold stroke-[3]" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-2 justify-end border-t border-slate-100 dark:border-slate-800 pt-3 mt-1.5 font-mono text-xs font-bold uppercase tracking-wider">
              <button
                type="button"
                onClick={() => setIsCreatingNotebook(false)}
                className="px-4 py-2 border rounded-lg text-slate-600 hover:bg-slate-50 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-[#1e222b] hover:bg-black text-white rounded-lg transition-transform active:scale-[0.98] cursor-pointer"
              >
                Create
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 5. RECYCLE BIN TRASH MODAL DIALOG */}
      <AnimatePresence>
        {showRecycleBin && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <RecycleBinModal
              onClose={() => setShowRecycleBin(false)}
              deletedNotebooks={notebooks.filter((b) => b.isDeleted)}
              deletedCards={cards.filter((c) => c.isDeleted)}
              onRestoreNotebook={handleRestoreNotebook}
              onRestoreCard={handleRestoreCard}
              onPermanentDeleteNotebook={handlePermanentDeleteNotebook}
              onPermanentDeleteCard={handlePermanentDeleteCard}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* 6. ALARM TRIGGER RING OVERLAYALERT BOX */}
      {triggeredAlarmCard && (
        <div className="fixed bottom-6 right-6 bg-indigo-600 dark:bg-indigo-700 text-white rounded-2xl p-5 shadow-2xl border border-indigo-500 max-w-[320px] w-full z-100 animate-in slide-in-from-bottom-5 duration-300">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-white/10 rounded-full animate-bounce shrink-0">
              <Bell className="w-5 h-5 text-yellow-300 fill-yellow-300" />
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-[10px] uppercase font-mono font-bold tracking-widest text-[#fbd249]">Reminder notification</span>
              <h4 className="font-bold text-[14px] mt-0.5 leading-snug line-clamp-2 truncate">{triggeredAlarmCard.title || 'Untitled Sticky Note reminder'}</h4>
              <p className="text-[11px] text-white/75 mt-1 leading-normal line-clamp-2">{stripHtml(triggeredAlarmCard.textContent) || 'Time to complete scheduled task'}</p>
            </div>
          </div>
          <div className="flex gap-2 justify-end mt-4 text-[10px] font-mono font-bold uppercase tracking-wide">
            <button
              onClick={() => {
                // Snooze / push schedule task reminder 10 minutes in future
                const snoozeTime = Date.now() + 10 * 60 * 1000;
                const updated = cards.map((c) =>
                  c.id === triggeredAlarmCard.id ? { ...c, reminderTime: snoozeTime } : c
                );
                setCards(updated);
                syncCards(updated);
                setTriggeredAlarmCard(null);
              }}
              className="p-1 px-3 bg-white/10 hover:bg-white/15 rounded-md cursor-pointer transition-colors"
            >
              Snooze 10m
            </button>
            <button
              onClick={() => {
                // Clear alarm reminder timestamp config
                const updated = cards.map((c) =>
                  c.id === triggeredAlarmCard.id ? { ...c, reminderTime: undefined } : c
                );
                setCards(updated);
                syncCards(updated);
                setTriggeredAlarmCard(null);
              }}
              className="p-1 px-3 bg-slate-900/60 hover:bg-slate-900 rounded-md cursor-pointer transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* 7. MODAL: SECURITY PIN PASSCODE CREATOR */}
      {lockNotebookId && (
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white dark:bg-slate-900 rounded-2xl max-w-sm w-full p-6 shadow-2xl border border-slate-100 dark:border-slate-800 relative overflow-hidden"
          >
            <div className="absolute top-0 inset-x-0 h-1.5 bg-gradient-to-r from-amber-400 to-amber-600" />
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-amber-50 dark:bg-amber-950/30 flex items-center justify-center text-amber-600 mb-3 block">
                <Lock className="w-5 h-5 mx-auto" />
              </div>
              <h3 className="font-sans font-bold text-lg text-slate-900 dark:text-white">Security Passcode Lock</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 max-w-[280px]">
                Create a security lock passcode to prevent unauthorized exposure of your notes inside this journal space.
              </p>
            </div>

            <div className="mt-5">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1.5 text-center">Enter 4-Digit PIN Code</label>
              <input
                type="password"
                maxLength={4}
                placeholder="••••"
                value={lockPasscode}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, '');
                  setLockPasscode(val);
                  setLockError('');
                }}
                className="w-full text-center text-2xl tracking-widest font-sans font-bold p-3 bg-slate-50 border rounded-xl focus:outline-none dark:bg-slate-950 dark:border-slate-800 text-slate-800 dark:text-white"
                autoFocus
              />
              {lockError && <p className="text-rose-500 text-center text-[11px] mt-1.5 font-bold">{lockError}</p>}
            </div>

            <div className="flex gap-2 justify-end mt-6 text-xs font-mono font-bold uppercase tracking-wider">
              <button
                onClick={() => {
                  setLockNotebookId(null);
                  setLockPasscode('');
                  setLockError('');
                }}
                className="px-4 py-2 border rounded-lg text-slate-600 hover:bg-slate-50 cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (lockPasscode.length !== 4) {
                    setLockError('Passcode must be exactly 4 numeric codes.');
                    return;
                  }
                  const updated = notebooks.map((b) =>
                    b.id === lockNotebookId ? { ...b, isLocked: true, pinCode: lockPasscode } : b
                  );
                  setNotebooks(updated);
                  localStorage.setItem(LOCAL_STORAGE_NOTEBOOKS_KEY, JSON.stringify(updated));
                  setLockNotebookId(null);
                  setLockPasscode('');
                }}
                className="px-5 py-2 bg-slate-900 dark:bg-amber-600 text-white dark:text-slate-950 rounded-lg cursor-pointer"
              >
                Lock Active
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* 8. MODAL: PASSCODE SECURE PROMPT */}
      {unlockingId && (
        <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white dark:bg-slate-900 rounded-2xl max-w-sm w-full p-6 shadow-2xl border border-slate-100 dark:border-slate-800 relative"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-full bg-rose-50 dark:bg-rose-950/30 flex items-center justify-center text-rose-650 mb-3 animate-pulse block">
                <Lock className="w-5 h-5 mx-auto text-rose-600 fill-rose-600/10" />
              </div>
              <h3 className="font-sans font-bold text-lg text-slate-900 dark:text-white">Notebook Locked</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Enter your 4-digit PIN Code to unlock this secure space.
              </p>
            </div>

            <div className="mt-5">
              <input
                type="password"
                maxLength={4}
                placeholder="••••"
                value={unlockPasscode}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, '');
                  setUnlockPasscode(val);
                  setUnlockingError('');
                }}
                className="w-full text-center text-2xl tracking-widest font-sans font-bold p-3 bg-slate-50 border rounded-xl focus:outline-none dark:bg-slate-950 dark:border-slate-800 text-slate-800 dark:text-white"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const book = notebooks.find((b) => b.id === unlockingId);
                    if (book && book.pinCode === unlockPasscode) {
                      setSelectedNotebookId(unlockingId);
                      setEditingCard(null);
                      setUnlockingId(null);
                      setUnlockPasscode('');
                    } else {
                      setUnlockingError('Incorrect security lock code.');
                    }
                  }
                }}
              />
              {unlockError && <p className="text-rose-500 text-center text-[11px] mt-1.5 font-bold">{unlockError}</p>}
            </div>

            <div className="flex gap-2 mt-6 text-xs font-mono font-bold uppercase tracking-wider">
              <button
                onClick={() => {
                  setUnlockingId(null);
                  setUnlockPasscode('');
                  setUnlockingError('');
                }}
                className="flex-1 py-2.5 border rounded-lg text-slate-600 hover:bg-slate-50 cursor-pointer text-center"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const book = notebooks.find((b) => b.id === unlockingId);
                  if (book && book.pinCode === unlockPasscode) {
                    setSelectedNotebookId(unlockingId);
                    setEditingCard(null);
                    setUnlockingId(null);
                    setUnlockPasscode('');
                  } else {
                    setUnlockingError('Incorrect security lock code.');
                  }
                }}
                className="flex-1 py-2.5 bg-rose-600 text-white rounded-lg cursor-pointer text-center"
              >
                Unlock
              </button>
            </div>
            <p className="text-[10px] text-slate-400 text-center mt-3 leading-tight select-none font-sans font-medium">
              Demo bypass hint: input whatever 4-digit code you configured for this notebook cover lock!
            </p>
          </motion.div>
        </div>
      )}

      {/* 9. MODAL: CHANGE COVER GALLERY SELECTOR */}
      {changingCoverNotebookId && (
        <div className="fixed inset-0 bg-slate-950/45 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white dark:bg-slate-900 rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-slate-100 dark:border-slate-800 flex flex-col gap-4"
          >
            <div className="flex items-center justify-between border-b pb-3 border-slate-105 dark:border-slate-800">
              <h3 className="font-sans font-bold text-lg text-slate-900 dark:text-white">Change Notebook Cover</h3>
              <button onClick={() => setChangingCoverNotebookId(null)} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full text-slate-400">
                <X className="w-5 h-5 cursor-pointer" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              <div className="flex flex-col items-center">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Live preview</span>
                <NotebookCover
                  title={notebooks.find(b => b.id === changingCoverNotebookId)?.title || "Journal"}
                  coverStyle={{
                    type: selectedChangeCoverPreset.type as 'gradient' | 'pattern' | 'solid' | 'image',
                    primaryColor: selectedChangeCoverPreset.primaryColor,
                    secondaryColor: selectedChangeCoverPreset.secondaryColor,
                    patternName: selectedChangeCoverPreset.patternName,
                    imageTheme: (selectedChangeCoverPreset as any).imageTheme
                  }}
                  className="scale-[0.80] origin-center -my-6"
                />
              </div>

              <div className="space-y-4">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block">Select New Artwork Theme</label>
                <div className="grid grid-cols-4 gap-2">
                  {COVERS_PRESETS.map((pst, idx) => {
                    const buttonBg = pst.type === 'gradient'
                      ? { backgroundImage: `linear-gradient(135deg, ${pst.primaryColor}, ${pst.secondaryColor})` }
                      : { backgroundColor: pst.primaryColor };

                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setSelectedChangeCoverPreset(pst)}
                        className={`w-[48px] h-[64px] rounded-r-md border border-black/10 shadow-xs relative overflow-hidden focus:outline-none transition-all ${selectedChangeCoverPreset.name === pst.name ? 'ring-2 ring-indigo-500 ring-offset-2 scale-105' : 'hover:scale-[1.03]'
                          }`}
                        style={buttonBg}
                        title={pst.name}
                      >
                        <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-black/15" />
                        <div className="absolute left-[2px] top-0 bottom-0 w-[0.5px] bg-white/20" />
                        {selectedChangeCoverPreset.name === pst.name && (
                          <div className="absolute inset-0 bg-black/10 flex items-center justify-center">
                            <Check className="w-4 h-4 text-white font-bold stroke-[3]" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex gap-2 justify-end border-t border-slate-100 dark:border-slate-800 pt-3 text-xs font-mono font-bold uppercase tracking-wider">
              <button
                onClick={() => setChangingCoverNotebookId(null)}
                className="px-4 py-2 border rounded-lg text-slate-650 hover:bg-slate-50 cursor-pointer text-slate-500 hover:text-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const updated = notebooks.map((b) =>
                    b.id === changingCoverNotebookId
                      ? {
                        ...b,
                        coverStyle: {
                          type: selectedChangeCoverPreset.type as 'gradient' | 'pattern' | 'solid' | 'image',
                          primaryColor: selectedChangeCoverPreset.primaryColor,
                          secondaryColor: selectedChangeCoverPreset.secondaryColor,
                          patternName: selectedChangeCoverPreset.patternName,
                          imageTheme: (selectedChangeCoverPreset as any).imageTheme
                        }
                      }
                      : b
                  );
                  setNotebooks(updated);
                  localStorage.setItem(LOCAL_STORAGE_NOTEBOOKS_KEY, JSON.stringify(updated));
                  setChangingCoverNotebookId(null);
                }}
                className="px-5 py-2 bg-slate-900 dark:bg-indigo-650 text-white rounded-lg cursor-pointer"
              >
                Apply Cover
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* 10. MODAL: NOTEBOOK INFO PROPERTY DETAILS SHEET */}
      {infoNotebook && (
        <div className="fixed inset-0 bg-slate-950/45 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white dark:bg-slate-900 rounded-2xl max-w-sm w-full p-6 shadow-2xl border border-slate-100 dark:border-slate-800 relative flex flex-col gap-4 select-none"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b pb-3 border-slate-100 dark:border-slate-800">
              <h3 className="font-sans font-bold text-lg text-slate-900 dark:text-white flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-500 shrink-0" />
                <span>Notebook Info</span>
              </h3>
              <button onClick={() => setInfoNotebook(null)} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full text-slate-400">
                <X className="w-5 h-5 cursor-pointer" />
              </button>
            </div>

            <div className="flex items-center gap-4 bg-slate-50 dark:bg-slate-950 p-3 rounded-xl border border-slate-100 dark:border-slate-850">
              <div className="w-18 h-24 shrink-0 pointer-events-none scale-90 origin-left">
                <NotebookCover
                  title={infoNotebook.title}
                  coverStyle={infoNotebook.coverStyle}
                  cardCount={notebookCardsCounter[infoNotebook.id] || 0}
                />
              </div>
              <div className="min-w-0 flex-1 text-left font-sans text-xs">
                <h4 className="font-bold text-sm text-slate-900 dark:text-white truncate">{infoNotebook.title}</h4>
                <p className="text-slate-400 mt-0.5 tracking-wider font-semibold uppercase text-[9px] font-mono select-all">ID: {infoNotebook.id}</p>
                <div className="flex flex-col gap-1.5 mt-2.5">
                  <div className="flex justify-between items-center bg-white dark:bg-slate-900 p-1 px-2 rounded-md shadow-2xs border border-slate-100 dark:border-slate-800">
                    <span className="text-slate-400 font-bold scale-90 origin-left text-[10px]">LOCKED:</span>
                    <span className="font-extrabold text-[#f39c12]">{infoNotebook.isLocked ? '🔒 Engaged' : '🔓 Open'}</span>
                  </div>
                  <div className="flex justify-between items-center bg-white dark:bg-slate-900 p-1 px-2 rounded-md shadow-2xs border border-slate-100 dark:border-slate-800">
                    <span className="text-slate-400 font-bold scale-90 origin-left text-[10px]">SHARED:</span>
                    <span className="font-extrabold text-[#3498db]">{infoNotebook.isShared ? '👥 Active' : '👤 Private'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-2.5 text-xs text-left">
              <div className="flex justify-between py-1 border-b border-dashed border-slate-100 dark:border-slate-800">
                <span className="text-slate-500 font-medium">Created On:</span>
                <span className="font-semibold text-slate-700 dark:text-white">{new Date(infoNotebook.createdAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-dashed border-slate-100 dark:border-slate-800">
                <span className="text-slate-500 font-medium">Total Notes Count:</span>
                <span className="font-semibold text-slate-700 dark:text-white">{notebookCardsCounter[infoNotebook.id] || 0} cards cataloged</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-slate-500 font-medium">Visual Format:</span>
                <span className="font-semibold text-slate-700 dark:text-white capitalize">{infoNotebook.coverStyle.type} theme illustration</span>
              </div>
            </div>

            <div className="flex justify-end mt-2 font-mono text-xs font-bold uppercase tracking-wider">
              <button
                onClick={() => setInfoNotebook(null)}
                className="px-5 py-2.5 bg-slate-900 text-white rounded-xl cursor-pointer"
              >
                Close Details
              </button>
            </div>
          </motion.div>
        </div>
      )}

    </div>
  );
}
